/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Component } from 'react';
import { 
  buildErrorMsgFromForm, 
  DateTool, 
  getConfig, 
  getManifestUrl, 
  getRawLicense, 
  isDev,
  isStage,
  isLocal,
  jparse, 
  orderHasManifest, 
  orderIsCheckedIn, 
  orderIsCheckedOut, 
  orderIsRejected, 
  stopProp, 
  uid, 
  userHasRole,
  size,
  cleanCopy,
  uploadManifestWithConfirm,
  wait,
  userIsSuspended,
  orderHasReconForm,
  getEnvLabel,
} from '../../actions/utils';
import { withRouter } from 'react-router-dom';
import { Form, Input, Alert, Select, AutoComplete, DatePicker, Modal, message, Icon, Popover, Upload, Checkbox } from 'antd';
import Bugsnag from '@bugsnag/js';
import SStyles from '../../_shared/styles';
import { Button } from '../../_shared/button';
import styles from './order.styles';
import { get, uniqBy, set, first, upperFirst, flatten, merge, uniq, orderBy, unset } from 'lodash';
import { createOrder, updateOrder, deleteOrder, cancelOrder, getOrder, signManifest, signRecon } from '../../_shared/services/orders.service';
import ScreenTitle from '../../_shared/screen-title';
import { _Order, deliveryStatus, OrderStatus, Rejections } from '../../models/order';
import Link from '../../_shared/link';
import LNSearch from '../../_shared/ln-search';
import RouteSearch from '../../_shared/route-search';
import moment from 'moment';
import { sendEmail } from '../../_shared/services/email.service';
import colors from '../../_shared/colors';
import XLSX from 'xlsx';
import config from '../../config';
import { getUsersMatchingLn } from '../../_shared/services/authentication.service';
import { getRoutes } from '../../_shared/services/routes.service';
import { ROUTECOLS } from '../route/route.component';
import DriverSearch from '../../_shared/driver-search';
import { PrintLabelsButton } from '../../_shared/printlabels';
import Row from '../../_shared/row';
import ScreenComponent from '../../_shared/hooks/useScreen';
import { Sigpad } from '../../_shared/sigpad';
import { getMidDate } from '../schedule/schedule.component';
// Card #280 round 2 — Auto Approval lead time / cutoff window for new orders.
import autoApprovalDates from '../../actions/autoapprovaldates';
import {
  describeBlackoutForMoment,
  getBlackoutConfig,
  loadBlackoutDates,
  toBlackoutDayString,
} from '../../_shared/services/blackoutdates.service';

export const SHOULDSIGN = [OrderStatus.fullrejection, OrderStatus.partialrejection, OrderStatus.delivered, OrderStatus.inTransit];

const DISABLED_MSG = 'If changes are required, please cancel this order and submit a new one. Please note that shipping labels will also need to be updated.';

const RETURN_LOCKED_MSG = 'This field is locked for return / pickup orders.';

// Card #266 — "New License Added" admin notification.
//
// Only handlers that exist to REACT to a value may be auto-fired by the bulk
// spreadsheet importer (processData). Anything with a side effect outside the
// form — email, persistence — must be driven by a real user action. Firing
// onSaveNew from there is what mailed admins 22 bogus notices from a single
// 13-row upload, each carrying the license NUMBER where a license RECORD was
// expected. `disabledDate` is a predicate, not a handler, and was never
// meaningfully callable here either.
const AUTO_INVOKABLE_INPUT_PROPS = ['onChange', 'onNM', 'onSelect'];

// Card #254 — the five order date fields a blackout applies to, with the label
// used in the admin override prompt. The bulk spreadsheet importer suffixes
// each with `__<n>`, which the submit-gate scan strips before looking up here.
const BLACKOUT_DATE_FIELDS = {
  preferredDelivery: 'Preferred Delivery Date',
  preferredPickup: 'Preferred Pickup Date',
  pickupDate: 'Pickup Date',
  dropoffDate: 'Drop Off Date',
  deliveryDate: 'Delivery Date',
};
const BLACKOUT_DATE_FIELD_RE = new RegExp(`^(${Object.keys(BLACKOUT_DATE_FIELDS).join('|')})(__\\d+)?$`);

// Card #284 — ONE predicate for "is this a blackout-checked date field", shared
// by the submit gate and the bulk spreadsheet importer so the two can never
// disagree about which fields are policed. Accepts either a bare key under
// `order` (`preferredDelivery`, the shape the submit gate scans) or a whole
// form var (`order.preferredDelivery__3`, the shape processData holds), because
// those are the only two namings this field ever has in this file.
//
// Returns { label, row } or null. `row` is the ZERO-BASED bulk row, or null on
// the single-order form.
const blackoutDateField = (name) => {
  const match = BLACKOUT_DATE_FIELD_RE.exec(`${name}`.replace(/^order\./, ''));
  if (!match) { return null; }
  return {
    label: BLACKOUT_DATE_FIELDS[match[1]],
    row: match[2] ? Number(match[2].slice(2)) : null,
  };
};

// 'Preferred Delivery Date (order 3) — Thu, Nov 26, 2026 is blacked out:
// Thanksgiving Day.' One sentence builder for every blackout surface on this
// screen: the admin override confirm, the non-admin submit block and the
// importer's per-row field error all read identically.
const blackoutSentence = (field, day, hit) => {
  const which = field.row === null ? '' : ` (order ${field.row + 1})`;
  const pretty = moment(day).format('ddd, MMM D, YYYY');
  return `${field.label}${which} — ${pretty} is blacked out: ${hit.label}.`;
};

const BLACKOUT_BLOCKED_MSG = 'Blacked-out days are not available for scheduling. Please correct the date and upload again.';

// Licenses already announced in this browser session — a genuine multi-license
// add should not become a thread of near-identical emails.
const NOTIFIED_LICENSES = new Set();

const escapeHtml = v => `${v}`
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// Accepts either a license record or a bare license number, so a future
// mis-call degrades to a legible subject instead of "(undefined)".
const licenseNumberOf = nl => (
  typeof nl === 'string' ? nl.trim() : `${get(nl, 'License #', '')}`.trim()
);

const licenseAddedSubject = licNum => {
  // Tenant name (Edit Settings -> emails.subjectPrefix, the same key every other
  // transactional email uses) plus the deployment tier, so a recipient can tell
  // a stage test from a production event at a glance.
  const prefix = [getConfig('emails.subjectPrefix'), `[${getEnvLabel()}]`]
    .filter(f => !!f)
    .join(' ');

  return `${prefix} - New License Added (${licNum || 'unknown'})`;
};

const licenseAddedBody = (nl, licNum, addedBy) => {
  const address = [get(nl, 'Street Address'), get(nl, 'City'), get(nl, 'Zip')]
    .filter(f => !!f)
    .join(', ');

  const rows = [
    ['License #', licNum || 'unknown'],
    ['Licensee', get(nl, 'Licensee')],
    ['DBA', get(nl, 'DBA')],
    ['Address', address],
    ['Site', getConfig('emails.subjectPrefix') || getEnvLabel()],
    ['Added by', addedBy],
    ['Added', moment().format('MMM D, YYYY h:mm a')],
  ].filter(([, v]) => !!v);

  const cells = rows
    .map(([k, v]) => `<tr><td valign="top"><b>${escapeHtml(k)}:</b></td><td>${escapeHtml(v)}</td></tr>`)
    .join('');

  return `A new License has been added by a user.<br /><br /><table cellpadding="4">${cells}</table>`;
};

// Card #256 round 7 — MASTER switch for the whole "Payment Requirement for
// Delivery" feature (Danielle 2026-08-11: "if toggled off then this field is not
// displayed anywhere on the platform"). OFF suppresses the ENTIRE feature on this
// screen, not merely the Profile field: the "Payment Required?" checkbox (even for
// an order already carrying data.order.paymentRequired), both per-order payment
// contact fields, the Admin override checkbox and its reason, the Disclaimer /
// Payment Override notices, the override audit stamp, and the client completion
// guard.
// The feature defaults to OFF on every environment. settingsConfig.json's
// `defaultValue` never reaches runtime — client getConfig is lodash `get` over
// window.APPCONFIG (actions/utils.js) — so the `false` literal below IS the
// effective default, and it must stay byte-identical to the fallback used by every
// other screen reading this key (profile / orders / schedule / operations log) or
// the surfaces disagree about what "unset" means.
// Read through a function, never a module-level constant: window.APPCONFIG is
// populated after this module is imported.
const paymentRequirementEnabled = () => !!getConfig('orders.paymentRequirementEnabled', false);

// Card #256 — exact payment enforcement error text (round 3 wording — de-COD'd).
// Kept in LOCKSTEP with the authoritative server guard (orders/utils.js
// COD_PAYMENT_ERROR). Also used to RECOGNIZE a server-side block (matched with
// includes(), since save errors can come back \n-joined) so the big driver
// popup is raised for that path too.
const COD_PAYMENT_ERROR = 'Payment Required before this order can be completed.';

// Card #256 round 5 — the statuses that may not be reached while payment is
// still outstanding. delivered(3) AND partialrejection(6) only: a partial
// rejection means goods WERE handed over so the money is still owed (Danielle
// 2026-07-30, "The partial rejection order type should still require payment").
// fullrejection(5) and exception(4) are deliberately EXCLUDED — the customer
// took nothing, so blocking them would strand the driver at the door with no
// way to record the outcome and would also stop the rejection return from ever
// being created.
// Kept in LOCKSTEP with the authoritative server guard in
// server/src/controllers/orders/utils.js (PAYMENT_REQUIRED_STATUSES), which
// also covers the native WebView driver.
const PAYMENT_REQUIRED_STATUSES = [OrderStatus.delivered, OrderStatus.partialrejection];

const LOCKED_STATUSES = [
  OrderStatus.orderApproved,
  OrderStatus.inTransit,
  OrderStatus.delivered,
  OrderStatus.exception,
  OrderStatus.fullrejection,
  OrderStatus.partialrejection,
  OrderStatus.orderPickedUp,
  OrderStatus.orderCancelled,
];

const RETURN_PICKUP_TYPES = ['rej_return', 'cash_return', 'cash_pickup', 'other_return'];

// Card #260 — statuses a check-in is allowed to auto-advance to Order Picked Up.
// This MUST stay an explicit whitelist and never become a `status <
// OrderStatus.orderPickedUp` comparison: OrderStatus is not lifecycle-ordered
// (orderPickedUp is 7 while delivered is 3, exception 4, rejections 5/6,
// cancelled 8), so an ordering check would drag already-finished orders
// backwards. orderReceived is excluded on purpose — advancing it would bypass
// approval and make submit() fabricate a dateApproved stamp.
const CHECKIN_AUTO_PICKUP_FROM = [OrderStatus.orderApproved];

// Card #151 round 2 — the customer-facing order emails (Order Update, Order
// Rejected) go to the pickup account's Account Admins and Users only. Roles
// 0/1/2 attached to the same license are internal staff and no longer receive
// the customer copy.
const CUSTOMER_ALERT_ROLES = [3, 4];

// Card #151 round 4 — the statuses at which the Customer Delivery Confirmation
// ("Delivery Dispensary Email") is sent. Mirrors `delStatus` inside
// sendManifestEmails (server/src/controllers/orders/index.js) so the client only
// calls /signmanifest when the server will actually mail something; the server
// remains authoritative and re-checks it.
const DELIVERY_CONFIRMATION_STATUSES = [
  OrderStatus.delivered,
  OrderStatus.partialrejection,
  OrderStatus.fullrejection,
];

// Card #151 round 6 — order types that must NEVER send the Customer Delivery
// Confirmation ("Delivery Dispensary Email"). Round 4 decoupled that email from
// the manifest PDF, which made it start firing on returns: a return travels BACK
// to the vendor, so the greeting (the destination licensee — on a return that is
// the original PICKUP company), the body ("Your recently completed delivery has
// been confirmed") and the POD recipient list are all wrong for it
// (Danielle 2026-08-03). Suppression is unconditional — it does not matter
// whether the order carries a signed manifest PDF.
// Per-client behaviour belongs in Edit Settings, so the live list comes from
// `emails.deliveryConfirmationExcludeTypes` (stringArray) and this constant is
// only the fallback used when the key is absent from the saved config — which
// means the default behaviour is already correct with no config write.
// Deliberately these two only: `cash_pickup` and `other_return` are unchanged.
const DELIVERY_CONFIRMATION_EXCLUDED_TYPES = ['cash_return', 'rej_return'];

// True when this order type is excluded from the delivery confirmation email.
// getConfig is lodash `get`, which only substitutes the fallback for `undefined`,
// so a null / non-array value saved against the key is coerced back to the
// constant above; an explicitly EMPTY list is honoured and means "exclude
// nothing".
const isDeliveryConfirmationExcludedType = (orderType) => {
  const configured = getConfig('emails.deliveryConfirmationExcludeTypes', DELIVERY_CONFIRMATION_EXCLUDED_TYPES);
  const excluded = Array.isArray(configured) ? configured : DELIVERY_CONFIRMATION_EXCLUDED_TYPES;
  return excluded.includes(orderType);
};

const ROLE_LABELS = { 3: 'Account Admin', 4: 'User' };

const toArray = v => (Array.isArray(v) ? v : (v ? [v] : []));

// Card #291 — "<label>: <DBA> (<License #>)". This is the row the Order Canceled
// email has carried since card #151 round 9, and the card asks for the same one
// on the Order Update / Order Rejected / Order Partially Rejected emails
// ("There is an example of this being used currently on Order Cancelled"), so it
// is factored out here and every email that shows it renders it through this one
// function instead of four copies drifting apart.
//
// Neither half can be trusted to be a non-empty string, and neither can be
// defaulted with _.get: lodash only substitutes its default for `undefined`, so
// a stored null or '' reaches the template unchanged — which is what produced
// the dangling "Destination:  ()" the interpolated version emits today. A
// license-less company prints its name alone, a nameless one prints just
// "(LN)", and a company with neither drops the whole row rather than mailing a
// bare label. DBA falls back to Licensee (the same order of preference as
// server/src/controllers/orders/reconciliation.js) because only some states
// populate both — NJ, the tenant on the card, writes the facility name to each.
const companyLine = (label, company) => {
  const name = `${get(company, 'DBA') || get(company, 'Licensee') || ''}`.trim();
  const ln = `${get(company, 'License #') || ''}`.trim();

  if (!name && !ln) { return '' }

  const parts = [name ? escapeHtml(name) : '', ln ? `(${escapeHtml(ln)})` : ''];

  return `${label}: ${parts.filter(f => !!f).join(' ')}`;
};

// The destination row for an order RECORD (`{ id, orderId, data: { ... } }`),
// which is the shape every caller on this screen holds.
const destinationLine = order => companyLine('Destination', get(order, 'data.destCompany'));

// De-dupes on a lowercased/trimmed address while mailing the FIRST spelling
// seen, so a box that is both a user account and an Edit Settings entry (or is
// cased differently in the two places) is only mailed once.
const dedupeRecipients = (recipients = []) => {
  const seen = {};
  return recipients.filter(r => {
    // `|| ''` rather than a _.get default: _.get only substitutes for
    // undefined, so a literal null would stringify to a truthy "null" key.
    const key = `${get(r, 'email') || ''}`.trim().toLowerCase();
    if (!key || seen[key]) { return false }
    seen[key] = true;
    return true;
  });
}

// Non-production hosts must never mail live cannabis operators, so sendEmail
// keeps redirecting every send to conf.devEmails. That redirect also hides WHO
// the email would have reached, which is the only thing worth testing here — so
// on those hosts the intended recipients are prepended to the body.
//
// Card #151 round 8 — the tag is the live tier (getEnvLabel) rather than a
// hardcoded [DEV], because this now runs on stage as well; see isGuardedHost.
//
// GUARD_MARKER is the literal from server/src/actions/mailguard.js. The server
// funnel guard re-checks every send and stamps its own banner unless it finds
// this marker — this block names each recipient's role, which the server cannot
// see, so it wins and the marker is what stops a second banner stacking on top.
// If you rename it here, rename it there in the same commit.
const GUARD_MARKER = 'data-gps-mail-guard';

// Card #151 round 8 — every non-production tier, not just dev. Kept in LOCKSTEP
// with GUARDED_APPS in server/src/actions/mailguard.js (STAGE / DEV / LOCAL):
// isDev() already covers localhost, so the two lists describe the same tiers by
// different means. Read as a function, never a module-level constant —
// window.location is not meaningful at import time in every host.
const isGuardedHost = () => isDev() || isStage();

// Card #151 round 9 — the label is "Would have sent to:", not round 8's "Would
// have gone to:". Danielle 2026-08-28 wrote the wording she expects twice in the
// same comment: "The customer email should be displayed as 'Would have sent to:
// [customer email]'". Renamed at all four sites in one change — here,
// server/src/actions/mailguard.js recipientBlock plus its fail-closed refusal
// reason and its console log, and server/src/controllers/dailyemail.js
// stageRecipientBlock. Round 8's invariant is that the three notices read
// identically to a tester, so they only ever move together.
//
// `note` is the round-9 addition and is optional: it is the one line that tells a
// tester WHY no customer is named (see noCustomerNote). Nothing else about the
// block changed.
const devRecipientBlock = (recipients = [], note) => `<div ${GUARD_MARKER}="1" style="border:1px solid #d9534f;padding:8px;margin-bottom:16px;font-family:monospace;">
<b>[${getEnvLabel()}]</b> Would have sent to: ${recipients.map(r => `${r.email} (${r.label})`).join(', ') || '(no one)'}${note ? `<br />${note}` : ''}
</div>`;

// Card #151 round 9 — Danielle 2026-08-28, testing "Would have gone to":
// "The customer email was not displayed as 'Would have sent to.'" The banner she
// saw listed only the configured admin boxes because the license resolved to no
// role 3/4 recipient at all — there was no customer in the list to display.
//
// A banner that names only the configured boxes has two completely different
// meanings and a tester cannot tell them apart: either this order's pickup
// account genuinely has no customer contact, or it has one and something
// excluded them (wrong role, or their own email alerts are off). Say which,
// so "the customer is missing" is answered in the email itself rather than in a
// round trip through the card.
const noCustomerNote = (ln, users = []) => {
  if (!ln) {
    return 'No customer recipients: this order carries no pickup license number, so none could be looked up.';
  }

  if (!users.length) {
    return `No customer recipients: no user account is attached to license ${ln}.`;
  }

  return `No customer recipients: license ${ln} has ${users.length} user account${users.length === 1 ? '' : 's'}, but none of them is an Account Admin or Standard User with email alerts switched on.`;
};

// Card #151 round 9 — the Edit Settings "Admin Email Recipients" (adminEmails)
// list, resolved for one send.
//
// Danielle 2026-08-28: "The Admin Order Date Change email is working in PROD. The
// same email is not being received in STAGE." An empty admin list produces
// exactly that symptom, and produces it almost silently: the server rejects a
// send with no recipients (server/src/controllers/email.js "Required settings not
// sent") before the mail guard ever runs, and the callers below neither awaited
// nor caught, so the 500 escaped the enclosing try as an unhandled promise
// rejection — no toast, and no log naming the order or the send, only the
// browser's generic "Uncaught (in promise)" line.
//
// Round 9 first wrote that the failure reached "no Bugsnag" either. That was
// wrong, and round-9 validation proved it in the built bundle: @bugsnag/browser
// auto-captures unhandled rejections by default (its enabledErrorTypes default
// for them is true, and App.js:17-22 never overrides it), and getEnv() reports
// 'prod' on stage as well as on production, so Bugsnag did
// receive the error — unnamed and uncorrelated, but it received it. Adding a
// .catch to those sends consumes the rejection, and would have taken that report
// with it, so every handler below now calls Bugsnag.notify(err) by hand: the
// console.warn is the local, named signal, and the notify keeps the deployed-tier
// report the un-awaited call used to raise for free.
//
// An empty list still means "send to nobody". It is deliberately NOT swapped for
// the built-in config().adminEmails list: that would resurrect
// orders@/warehouse@greenparcelservice.com as live recipients on any tenant that
// cleared the field on purpose, which is a real-mail change on live sites. The
// fix is to make the silence loud, not to change who gets mail.
//
// getConfig is lodash `get`, which substitutes the fallback only for `undefined`
// — an ABSENT key therefore still falls back to config().adminEmails exactly as
// it always has. The Array.isArray guard is for the `null` a full-document
// Settings save can write, which today spreads straight into a TypeError.
const adminRecipients = (context) => {
  const configured = getConfig('adminEmails', config().adminEmails);
  const list = (Array.isArray(configured) ? configured : []).filter(e => !!e);

  if (!list.length) {
    const warning = `${context} was not sent: the "Admin Email Recipients" setting (adminEmails) is empty on ${getEnvLabel()}. Set it under Edit Settings > Email.`;
    console.warn(`[card #151] ${warning}`, { adminEmails: configured });
    message.warning(warning, 8);
  }

  return list;
};

class OrderComponent extends Component {
  constructor(props) {
    super(props);

    const self = this;

    // Card #256 round 4 — payment contact field wiring. Roles are fixed for the
    // life of the form, so both are resolved once here (toDisplay is built in
    // this constructor and `info` is read as a value by the renderer).
    // Editable by Account Admins (3) + Standard Users (4) only; everyone else
    // who can see the fields gets them read-only.
    const canEditPaymentContact = userHasRole([3, 4], props.sState);
    // Hover disclaimer — shown to the roles that own the profile copy of these
    // values, per the mockup ("a disclaimer on hover for Account Admins and
    // Standard Users").
    const paymentContactHelp = canEditPaymentContact
      ? 'Payment contact information is managed on the Profile screen. If the contact name or phone number is incorrect, please update it on the Profile screen.'
      : undefined;

    this.state = {
      error: null,
      success: null,
      loading: true,
      isEdit: false,
      order: null,
      imageUrl: '',
      cancelModal: false,
      // Card #256 round 2 — big driver-facing popup shown when COD enforcement
      // blocks completing a delivery (client guard or server guard).
      codBlockModal: false,
      checkoutModal: { visible: false },
      destZip: null,
      pickupZip: null,
      allRoutes: [],
      showAllRoutes: false,
      showAllRoutesPickup: false,
      cancelInput: '',
      cashReturn: undefined,
      manifestUploading: false,
      notify: false,
      intNotes: '',
      toDisplay: [
        {
          title: 'Order Definition',
          var: 'order.orderDef',
          type: 'dropdown',
          canDisplay: () => getConfig('orders.isInternalDist', false),
          opts: () => {
            const opts = [
              { title: 'Internal', value: 'int' },
              { title: 'External', value: 'ext' },
            ];

            return opts;
          },
          inputProps: function () {
            const thisInp = this;
            return {
              onChange: function (v, o = {}) {
                setTimeout(() => {
                  if (!get(o, 'isAuto')) { 
                    self.setFieldValue(thisInp, 'order.orderType', v === 'int' ? 'sameDay' : 'layover');
                  }
                  if (v == 'int') {
                    self.setFieldValue(thisInp, 'order.status', OrderStatus.orderApproved);
                  }
                  self.getAllRoutes();
                }, 50);
              },
              disabled: userHasRole([2], props.sState),
            }
          },
          options: {
            rules: [  
              {
                required: true,
                message: 'Order Definition is required. ',
              },
            ],
          },
        },
        {
          title: () => get(this, 'state.cashReturn') ? 'Reconciliation Return Type' : 'Order Type',
          var: 'order.orderType',
          type: 'dropdown',
          opts: function () {
            const compute = function () {
            const odef = self.getOrderDef(this);

            const opts = [
              { title: 'Pickup with Layover', value: 'layover' },
              { title: 'Dropoff with Layover', value: 'dropoff' },
            ];

            const cashOptions = [
              { title: 'Reconciliation Return', value: 'cash_return' },
              { title: 'Rejection Return', value: 'rej_return' },
              { title: 'Payment Pickup', value: 'cash_pickup' },
              { title: 'Other', value: 'other_return' },
            ];

            if (self.state.cashReturn) {
              //(cash return, rejection return, other)
              return cashOptions;
            }

            const hasSameDay = getConfig('orders.hasSameDay', get(self.props, 'sState.user.sameDayPickup', false));
            const isAll = self.isAllAvail();

            const sameday = { title: 'Same Day', value: 'sameDay' };

            if (odef == 'int') {
              return [sameday];
            }

            if (hasSameDay || isAll) {
              opts.unshift(sameday);
            }

            if (isAll) {
              return [
                ...opts,
                ...cashOptions,
              ]
            }

            return opts;
            };

            let list = compute.call(this) || [];
            const current = self.getFieldValue(this, 'order.orderType');
            // Master Rejection Returns toggle (default on). When off, drop the
            // 'Rejection Return' option — but keep the currently-selected type
            // so existing rej_return orders still render their value.
            const rejReturnsEnabled = getConfig('orders.rejectionReturns.enabled', true);
            if (!rejReturnsEnabled) {
              list = list.filter(o => o.value !== 'rej_return' || o.value === current);
            }
            // Restrict to the configured enabled order types (empty = all).
            // Always keep the currently-selected type so existing orders
            // still render their value.
            const enabled = getConfig('orders.enabledOrderTypes', []);
            if (!enabled || !enabled.length) { return list; }
            return list.filter(o => enabled.includes(o.value) || o.value === current);
          },
          inputProps: () => {
            const currentOrderType = get(self, 'state.order.data.order.orderType');
            const isReturnOrPickup = RETURN_PICKUP_TYPES.includes(currentOrderType);
            const isReturnLocked = isReturnOrPickup && !userHasRole([0, 1], props.sState);
            const disabled = userHasRole([2], props.sState) || isReturnLocked;
            return {
              disabled,
              tooltip: isReturnLocked ? RETURN_LOCKED_MSG : null,
            };
          },
          options: {
            rules: [
              {
                required: true,
                message: 'Order Type is required. ',
              },
            ],
          },
        },
        {
          // Customer Pickup: for Reconciliation (cash_return) / Rejection
          // (rej_return) returns where the customer collects the order
          // themselves — no return route/driver/date is scheduled. When
          // checked, the Return Date/Route/Driver fields are hidden and their
          // values cleared on save (Danielle 2026-05-19).
          title: 'Customer Pickup',
          var: 'order.customerPickup',
          type: 'checkbox',
          canDisplay: function () {
            const orderType = self.getFieldValue(this, 'order.orderType');
            return ['cash_return', 'rej_return'].includes(orderType);
          },
          options: { valuePropName: 'checked' },
          inputProps: () => {
            const currentOrderType = get(self, 'state.order.data.order.orderType');
            const isReturnOrPickup = RETURN_PICKUP_TYPES.includes(currentOrderType);
            const isReturnLocked = isReturnOrPickup && !userHasRole([0, 1], props.sState);
            return {
              disabled: isReturnLocked,
              tooltip: isReturnLocked ? RETURN_LOCKED_MSG : null,
              onChange: (e) => {
                if (get(e, 'target.checked')) {
                  this.props.form.setFieldsValue({
                    'order.deliveryDate': undefined,
                    'order.route': undefined,
                    'order.driver.delivery': undefined,
                  });
                }
                this.setState({});
              },
            };
          },
        },
        {
          title: 'Originating License #',
          var: 'order.pickupLN',
          type: () => {
            const isInternal = getConfig('orders.isInternalDist', false);
            const isAdmin = userHasRole([0, 1], props.sState);
            const isDriverOrder = get(this, 'state.isDriverOrder');

            if (isInternal && isAdmin) {
              return this.state.isNewAdminOrder ? 'lnsearch' : 'dropdown';
            }

            return isDriverOrder ? 'lnsearch' : isAdmin ? 'lnsearch' : 'dropdown';
          },
          opts: () => {
            const userOpts = uniqBy([
              ...[get(this.props.sState.user, 'company[License #]')],
              ...get(this.props.sState.user, 'company.licenseNumbers', [])
            ]).map(ln => ({
              title: ln,
              value: ln
            }));
  
            return userOpts;
          },
          nmProps: {
            title: 'Originating License Address',
            var: 'order.pickupCompany[Street Address]',
            placeholder: 'Address',
            options: {
              validateTrigger: 'onBlur',
              rules: [
                {
                  required: true,
                  message: 'Originating License Address is required. ',
                },
              ],
            },
          },
          inputProps: function() { 
            const isLocked = LOCKED_STATUSES.includes(get(self, 'state.order.data.order.status'));
            const disabled = isLocked && !userHasRole([0, 1], props.sState);
            const forall = {
              disabled,
              tooltip: disabled ? DISABLED_MSG : null,
              showAddress: true,
              allowEdit: getConfig('orders.editAddress', false),
              onSaveNew: (nl, o) => self.notifyLicenseAdded(nl, o),
            }
            if (userHasRole(2, props.sState)) {
              const isDriverOrder = !!self.state.isDriverOrder;
              return {
                showAddress: true,
                allowEdit: forall.allowEdit && !isDriverOrder,
                disabled: !isDriverOrder,
              } 
            }
            if (!self.isAllAvail()) { return forall }
            return { 
              ...forall,
              onNM: (val) => self.onNM(val, this),
              filterActive: false,
            }
          },
          subText: () => {
            const { form: { getFieldValue } } = this.props;
            const orderType = getFieldValue('order.orderType');
            if (orderType === 'cash_pickup') {
              return <div css={css(styles.subText)}>Pickup Location</div>
            }
            return null;
          },
          options: {
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                message: 'License # is required. ',
              },
            ],
          },
        },
        {
          title: 'Destination License #',
          type: 'lnsearch',
          var: 'order.destinationLN',
          required: true,
          nmProps: {
            title: 'Destination License Address',
            var: 'order.destCompany[Street Address]',
            placeholder: 'Address',
            options: {
              validateTrigger: 'onBlur',
              rules: [
                {
                  required: true,
                  message: 'Destination License Address is required. ',
                },
              ],
            },
          },
          inputProps: function() { 
            const isLocked = LOCKED_STATUSES.includes(get(self, 'state.order.data.order.status'));
            const disabled = isLocked && !userHasRole([0, 1], props.sState);
            const forall = {
              disabled,
              tooltip: disabled ? DISABLED_MSG : null,
              showAddress: true,
              allowEdit: getConfig('orders.editAddress', false),
              onSelect: v => {
                const rl = getRawLicense(v);
                self.setState({ intNotes: get(rl, 'intNotes', '') });
              },
              onSaveNew: (nl, o) => self.notifyLicenseAdded(nl, o),
            }
            if (userHasRole(2, props.sState)) { 
              const isDriverOrder = !!self.state.isDriverOrder;
              return { 
                ...forall,
                allowEdit: forall.allowEdit && !isDriverOrder,
                disabled: !isDriverOrder,
              } 
            }
            if (!self.isAllAvail()) { return forall }
            return { 
              ...forall,
              onNM:  (val) => self.onNM(val, this),
              filterActive: false,
            }
          },
        },
        {
          title: 'Preferred Delivery Date',
          type: 'datepicker',
          var: 'order.preferredDelivery',
          info: 'Preferred delivery dates are subject to change at the discretion of Green Parcel Service. Any changes to delivery date will be communicated.',
          canDisplay: () => !get(this, 'state.cashReturn'),
          inputProps: function() {
            const inpThis = this;
            const isLocked = LOCKED_STATUSES.includes(get(self, 'state.order.data.order.status'));
            const disabled = isLocked && !userHasRole([0, 1], props.sState);
              
            return {
              disabled,
              tooltip: disabled ? DISABLED_MSG : null,
              disabledDate: (date) => {
                const orderDef = self.getFieldValue(inpThis, 'order.orderDef');

                // Card #254 — the blackout is a SECOND, global, SUBTRACTIVE layer
                // composed ON TOP of the existing rule. It never widens
                // availability and never replaces the per-ZIP route-day check.
                // It is OR'd on the OUTSIDE deliberately: `disabledDates` returns
                // false outright when isAllAvail() is set (the Schedule
                // quick-create modals), so anything folded inside it would be
                // skipped in exactly that path.
                if (orderDef == 'int') {
                  return moment(date).startOf('day').isBefore(moment().startOf('day')) || self.blackoutDisabled(date);
                }
                
                // Card #280 round 2 — route days, the blackout and the Auto
                // Approval lead time / cutoff window, each OR'd on the outside;
                // one method so the admin confirm can ask the same question.
                return self.preferredDeliveryDisabled(date, inpThis);
              },
              onChange: e => {
                const isEdit = !!get(this, 'state.order.id');
  
                if (!isEdit) {
                  const delVar = 'order.deliveryDate';
                  const delDate = self.getFieldValue(inpThis, delVar);
                  const delVis = self.state.bulkOrders.length > 0 ? !!delDate : self.state.toDisplay.find(td => td.var == delVar).canDisplay();
                  if (delVis) {
                    self.setFieldValue(inpThis, delVar, getMidDate(e));
                  }
                }
              }
            }
          },
          options: {
            rules: [
              {
                required: true,
                message: 'Preferred Delivery Date is required.',
              },
            ],
          },
        },
        {
          title: () => {
            const { form: { getFieldValue } } = this.props;
            const value = getFieldValue('order.orderType');
            if (value === 'rej_return') { return 'Rejected Date'; }
            return value === 'dropoff' ? 'Preferred Dropoff Date' : 'Preferred Pickup Date';
          },
          type: 'datepicker',
          var: 'order.preferredPickup',
          info: 'Preferred pickup dates are subject to change at the discretion of Green Parcel Service. Any changes to pickup date will be communicated.',
          inputProps: function () {
            const inpThis = this;
            const isLocked = LOCKED_STATUSES.includes(get(self, 'state.order.data.order.status'));
            const disabled = isLocked && !userHasRole([0, 1], props.sState);
              
            return {
              disabled,
              tooltip: disabled ? DISABLED_MSG : null,
              disabledDate: (date) => {
                const orderType = self.getFieldValue(inpThis, 'order.orderType');
                // Card #280 round 2 — Auto Approval lead time / cutoff window.
                if (self.autoApprovalDayDisabled(date, inpThis, 'preferredPickup')) { return true; }
  
                const isBefore = moment(date).startOf('day').isBefore(orderType === 'sameDay' ? moment().endOf('day') : moment().startOf('day'));
                // Card #254 — blackout composed on top of the existing past-date rule.
                return isBefore || self.blackoutDisabled(date);
              },
              onChange: e => {
                const isEdit = !!get(inpThis, 'state.order.id');
  
                if (!isEdit) {
                  const pickVar = 'order.pickupDate';
                  
                  const pickDate = self.getFieldValue(inpThis, pickVar);
                  const pickVis = self.state.bulkOrders.length > 0 ? !!pickDate : self.state.toDisplay.find(td => td.var == pickVar).canDisplay();
  
                  if (pickVis) {
                    self.setFieldValue(inpThis, pickVar, getMidDate(e));
                  }
                }
              }
            }
          },
          canDisplay: function () {
            const orderType = self.getFieldValue(this, 'order.orderType');
            if (orderType === 'cash_pickup') { return false; }
            return orderType != 'sameDay';
          },
          // inputProps: (item) => {
          //   const { form: { getFieldValue } } = this.props;
          //   const value = getFieldValue('order.orderType');

          //   if (value == 'sameDay') {
          //     return {
          //       disabledDate: (date) => {
          //         const isBefore = moment(date).startOf('day').isBefore(moment().endOf('day'));
          //         return isBefore;
          //       }
          //     }
          //   } else {
          //     return {};
          //   }
          // },
          options: {
            rules: [
              {
                required: getConfig('orders.autoApprove', false),
                message: 'Preferred Pickup Date is required.',
              },
            ],
          },
        },
        {
          title: 'Box count',
          var: 'order.weight',
          placeholder: '4',
          info: 'Please provide box or bundle count.',
          canDisplay: () => !get(this, 'state.cashReturn'),
        },
        {
          title: 'Notes',
          var: 'order.notes',
          type: 'textarea',
          placeholder: 
`- Temperature controlled delivery 
- Weight (Flower or Trim weight in lbs)
- Delivery time cutoff
- Special handling instructions
- etc...`,
          inputProps: () => ({ disabled: userHasRole(2, this.props.sState) }),
          styles: `clear: both;`,
          subText: () => {
            const { sState } = this.props;
            const isAdmin = userHasRole([0, 1, 2], sState);
            const intNotes = this.state.intNotes;

            if (!intNotes || !isAdmin) { return null }

            return (
              <div css={css(styles.subText)}>{intNotes}</div>
            )
          }
        },
        {
          title: 'Billing Amount',
          var: 'billing.amount',
          placeholder: 'Billing amount',
          canDisplay: () => {
            const orderType = this.props.form.getFieldValue('order.orderType') || get(this, 'state.order.data.order.orderType');
            if (orderType === 'cash_return' || orderType === 'rej_return') { return false; }
            return !get(this, 'state.cashReturn') || get(this, 'state.isDriverOrder') || orderType === 'cash_pickup';
          },
        },
        {
          title: 'Billing Invoice #',
          var: 'billing.invoice',
          placeholder: 'Billing Invoice number',
          canDisplay: () => {
            const orderType = this.props.form.getFieldValue('order.orderType') || get(this, 'state.order.data.order.orderType');
            if (orderType === 'cash_return' || orderType === 'rej_return') { return false; }
            return !get(this, 'state.cashReturn') || orderType === 'cash_pickup';
          },
          options: {
            rules: [  
              {
                required: getConfig('orders.billingInvoiceReq', true),
                message: 'Billing Invoice number is required. ',
              },
            ],
          },
        },
        {
          title: 'Billing Method',
          var: 'billing.method',
          placeholder: 'Billing Method',
          canDisplay: () => !get(this, 'state.cashReturn') && get(this, 'state.order.data.order.orderType') !== 'cash_pickup',
        },
        {
          title: 'Billing Method',
          var: 'billing.methodSelect',
          type: 'dropdown',
          opts: () => {
            const ot = get(this, 'state.order.data.order.orderType');
            const isCashPickup = ot === 'cash_pickup';
            const isCashReturn = ot === 'cash_return' || ot === 'rej_return';
            if (isCashPickup || isCashReturn) {
              return [
                { title: 'Cash', value: 'cash' },
                { title: 'Check', value: 'check' },
              ];
            }
            return [
              { title: 'Net Terms', value: 'net' },
              { title: 'Check', value: 'check' },
              { title: 'Cash', value: 'cash' },
              { title: 'Samples', value: 'samples' },
            ];
          },
          placeholder: 'Billing Method',
          canDisplay: () => {
            const ot = get(this, 'state.order.data.order.orderType') || this.props.form.getFieldValue('order.orderType');
            const isCashPickup = ot === 'cash_pickup';
            const isCashReturn = ot === 'cash_return' || ot === 'rej_return';
            if (isCashReturn) { return false; }
            return (!get(this, 'state.cashReturn') && userHasRole(2, this.props.sState)) || isCashPickup;
          },
        },
        {
          title: () => {
            const { form: { getFieldValue } } = this.props;
            const value = getFieldValue('billing.methodSelect');

            return value === 'check' ? 'Check #' : 'Cash bag #';
          },
          var: 'billing.methodSelectId',
          placeholder: 'Check or Cash bag #',
          canDisplay: () => {
            const { isEdit } = this.state;
            const { form: { getFieldValue }, sState } = this.props;
            const isDriver = userHasRole([2], sState);
            const value = getFieldValue('billing.methodSelect');

            const isDriverOrder = get(this, 'state.isDriverOrder');
            const ot = get(this, 'state.order.data.order.orderType') || getFieldValue('order.orderType');
            const isCashPickup = ot === 'cash_pickup';
            const isCashReturn = ot === 'cash_return' || ot === 'rej_return';

            if (isCashPickup || isCashReturn) {
              return value === 'cash' || value === 'check';
            }

            return isDriverOrder ? true : ((isEdit && isDriver) || isDriverOrder) && (value === 'cash' || value === 'check');
          }
        },
        {
          // Card #256 — per-order payment flag (mockup panel 2). Persists to
          // data.order.paymentRequired. Only shown when the order CREATOR's
          // account has "Payment Requirement for Delivery" = Allowed
          // (data.paymentRequirement). On create/own-order edit we read the
          // current user's setting directly; when an admin edits someone
          // else's order the creator's live setting isn't loaded client-side,
          // so we fall back to whether the order already carries the flag
          // (proof the creator's setting was on at creation).
          title: 'Payment Required?',
          var: 'order.paymentRequired',
          type: 'checkbox',
          options: { valuePropName: 'checked' },
          // text-align: left keeps the bare inline checkbox flush left — it
          // otherwise inherits the centering from `.App { text-align: center; }`.
          styles: `
            clear: both;
            text-align: left;
          `,
          canDisplay: function () {
            // Round 7 — master switch, tested FIRST. It has to sit ahead of the
            // `alreadyFlagged` arm below: an order saved while the feature was on
            // carries data.order.paymentRequired forever, and that arm would
            // otherwise leak the checkbox back onto the form after the master is
            // turned off. No data migration — the flag stays on the row, it is
            // just never surfaced or enforced.
            if (!paymentRequirementEnabled()) { return false }
            // Round 5 — a Rejection Return never shows the payment block, even
            // when it inherited a stale flag from the order it returns. Declared
            // as a method (not an arrow) so `this` is the toDisplay entry, which
            // is what carries the `__{i}` suffix on the bulk-upload path.
            // Round 6 — Reconciliation Returns (`cash_return`) too. This runs
            // BEFORE the alreadyFlagged test on purpose: a cash_return saved with
            // an explicit paymentRequired: false still counts as "flagged" there
            // (it is !== undefined), so a fix bolted onto that test would leave
            // the checkbox showing on re-edit.
            if (self.isPaymentExemptReturn(this)) { return false }
            const isEdit = self.state.isEdit;
            const currentUserId = get(self, 'props.sState.user.id');
            const orderUserId = get(self, 'state.order.userId');
            const creatorSettingOn = !!get(self, 'props.sState.user.paymentRequirement');
            const alreadyFlagged = get(self, 'state.order.data.order.paymentRequired') !== undefined;
            const creatorIsCurrent = !isEdit || !orderUserId || orderUserId === currentUserId;
            return (creatorIsCurrent && creatorSettingOn) || alreadyFlagged;
          },
          // Card #256 round 2 — (a) disclaimer shown while the box is CHECKED
          // (Danielle 7/14, typed wording verbatim); (b) Payment Override
          // notice whenever the loaded order carries data.order.paymentOverride
          // (deliberately shows just "an Admin" — no name/email). Round 3 —
          // tracks each bulk-suffixed checkbox (order.paymentRequired__{i}).
          // Round 4 — with the box ticked the notices move BELOW the two
          // payment contact fields (mockup panels 2/3), so the checkbox only
          // renders them when the box is clear (override-on-a-cleared-box).
          subText: (item) => self.renderPaymentNotices(item, 'checkbox'),
        },
        {
          // Card #256 round 4 — payment contact carried ON THE ORDER. Seeded
          // from the creator's profile when they tick "Payment Required?" and
          // then editable for THAT ORDER ONLY — saving never writes back to the
          // profile (mockup: "editable for that specific order does NOT
          // backfill data on profile"). Editable by Account Admins + Standard
          // Users (roles 3/4); Admins/Super Admins/drivers see it read-only.
          title: 'Payment Required Contact Name',
          var: 'order.paymentContactName',
          placeholder: 'Name',
          styles: `
            clear: both;
          `,
          info: paymentContactHelp,
          canDisplay: function () { return self.showPaymentContact(this) },
          options: function () { return self.paymentContactOptions(this) },
          inputProps: () => ({ disabled: !canEditPaymentContact }),
        },
        {
          title: 'Payment Required Contact Phone #',
          var: 'order.paymentContactPhone',
          placeholder: 'Number',
          styles: `
            clear: both;
          `,
          info: paymentContactHelp,
          canDisplay: function () { return self.showPaymentContact(this) },
          options: function () { return self.paymentContactOptions(this) },
          inputProps: () => ({ disabled: !canEditPaymentContact }),
          subText: (item) => self.renderPaymentNotices(item, 'contact'),
        },
        {
          // Card #256 — Admin / Super Admin override of the COD payment block.
          // Visible only to roles [0,1] and only for a payment-required order.
          // When checked (see submit assembly), stamps data.order.paymentOverride
          // {by,byId,at,reason}; the server audits it to operations_log.
          title: 'Override Payment Requirement (Admin)',
          var: 'order.paymentOverrideEnabled',
          type: 'checkbox',
          options: { valuePropName: 'checked' },
          // text-align: left — same inherited-centering fix as Payment Required?.
          styles: `
            clear: both;
            text-align: left;
          `,
          canDisplay: function () {
            // Round 7 — master switch. With the feature off nothing is ever
            // blocked for payment, so there is nothing to override; the flag on
            // an already-saved order must not resurrect this checkbox either.
            if (!paymentRequirementEnabled()) { return false }
            // Round 5 — nothing to override on a Rejection Return; it is never
            // blocked for payment in the first place. Round 6 — same for a
            // Reconciliation Return. Method form so `this` is the toDisplay
            // entry (bulk-upload `__{i}` suffix).
            if (self.isPaymentExemptReturn(this)) { return false }
            const { form: { getFieldValue }, sState } = self.props;
            const isAdmin = userHasRole([0, 1], sState);
            const flag = getFieldValue('order.paymentRequired');
            const paymentRequired = flag === undefined
              ? get(self, 'state.order.data.order.paymentRequired')
              : flag;
            return isAdmin && !!paymentRequired;
          },
        },
        {
          // Card #256 — optional reason captured alongside the override.
          title: 'Override Reason (optional)',
          var: 'order.paymentOverrideReason',
          placeholder: 'Reason for overriding the payment requirement',
          styles: `
            clear: both;
          `,
          canDisplay: () => {
            // Round 7 — master switch. Its parent checkbox is already hidden when
            // the feature is off (so the field it reads is never registered and
            // getFieldValue returns undefined), but gate it explicitly rather than
            // depend on rc-form's unregistered-name behaviour.
            if (!paymentRequirementEnabled()) { return false }
            const { form: { getFieldValue }, sState } = self.props;
            const isAdmin = userHasRole([0, 1], sState);
            return isAdmin && !!getFieldValue('order.paymentOverrideEnabled');
          },
        },
        {
          title: 'Manifest',
          var: 'billing.manifest',
          placeholder: getConfig('orderForm.billingManifestPlaceholder', 'Manifest number from METRC'),
          canDisplay: () => !get(this, 'state.cashReturn'),
          // inputProps: () => {
          //   // if (userHasRole(2, props.sState)) { return { disabled: true } }
          //   return {};
          // },
          inputProps: () => ({
            ...getConfig('orders.billingManifest', {
              disabled: true,
            })
          }),
          subText: () => {
            const { sState } = this.props;
            const isAdmin = userHasRole([0, 1], sState);
            const { file } = orderHasManifest(get(this, 'state.order'));

            const upl = {
              name: 'file',
              multiple: false,
              accept: ".pdf",
              showUploadList: false,
              beforeUpload: async (file) => {
                const conf = isAdmin ? window.confirm('Are you sure you want to upload a new manifest? This will overwrite the old one if one exists.') : true;

                if (conf) {
                  try {
                    this.setState({ manifestUploading: true });
                    await uploadManifestWithConfirm(file, this.state.order, isAdmin);
                    message.success('Manifest uploaded successfully!');
                    this.componentDidMount();
                  } catch (err) {
                    message.error(`Failed to upload manifest: ${err.message}`);
                  } finally {
                    this.setState({ manifestUploading: false });
                  }
                }
              }
            };

            return (
              <Row style={{ width: 'auto', position: 'absolute', right: 0, top: 0 }}>
                <Upload {...upl}>
                  <Button loading={this.state.manifestUploading} hoverText="Upload Manifest"><Icon type="upload" /></Button>
                </Upload>
                {!!file && <Button hoverText="View Manifest" onClick={() => window.open(getManifestUrl(file), '_blank')}><Icon type="file-protect" /></Button>}
              </Row>
            )
          }
        },
        //ADMIN SECTION
        {
          title: 'Delivery Status',
          var: 'order.status',
          type: 'dropdown',
          opts: () => {
            const isDriver = userHasRole(2, this.props.sState);
            let stats = [...deliveryStatus];
            if (isDriver) {
              stats = stats.filter(f => f.value !== OrderStatus.orderReceived);
            }
            return stats;
          },
          styles: `
            border-top: 5px solid ${colors.highlight};
            padding-top: 20px;
            clear: both;
          `,
          canDisplay: function () {
            if (self.isAlwaysShow('order.status', this)) { return true; }
            return self.state.isEdit && userHasRole([0, 1, 2], self.props.sState) && !self.isExternalOrder(this);
          },
          onChange: (value) => {
            const { sState } = this.props;
            const isDriver = userHasRole([2], sState);

            const showSigs = getConfig('sigs.show');
            const hasSig = get(this.props, 'sState.user.signature');

            if (!showSigs) { return }

            if (isDriver && !!hasSig && value === OrderStatus.orderPickedUp && !get(this, 'state.order.data.order.signature.pickup')) {
              const o = { ...this.state.order };
              set(o, 'data.order.signature.pickup', { ...hasSig, date: new Date().toISOString() });
              this.setState({ order: o })
            }

            if (isDriver && !!hasSig && [OrderStatus.delivered, OrderStatus.fullrejection, OrderStatus.partialrejection, OrderStatus.inTransit].includes(value)) {
              const o = { ...this.state.order };
              set(o, 'data.order.signature.driver', { ...hasSig, date: new Date().toISOString() });
              this.setState({ order: o })
            }
          }
        },
        {
          title: 'Rejected Reason',
          var: 'order.rejected',
          type: 'dropdown',
          placeholder: 'Reason for Rejection',
          opts: Rejections,
          styles: `
            clear: both;
          `,
          options: {
            rules: [
              {
                required: true,
                message: 'Reason for rejection is required. ',
              },
            ]
          },
          canDisplay: function () {
            const { isEdit } = self.state;
            const { form: { getFieldValue }, sState } = self.props;
            const isAdmin = userHasRole([0, 1, 2], sState);
            const value = getFieldValue('order.status');
  
            return isEdit && isAdmin && (value === OrderStatus.fullrejection || value === OrderStatus.partialrejection) && !self.isExternalOrder(this);
          }
        },
        {
          title: 'Rejected Text',
          var: 'order.rejectedText',
          type: 'textarea',
          placeholder: 'Reason for Rejection',
          opts: Rejections,
          styles: `
            clear: both;
          `,
          options: {
            rules: [
              {
                required: true,
                message: 'Reason for rejection is required. ',
              },
            ]
          },
          canDisplay: function () {
            const { isEdit } = self.state;
            const { form: { getFieldValue }, sState } = self.props;
            const isAdmin = userHasRole([0, 1, 2], sState);
            const value = getFieldValue('order.rejected');
  
            return isEdit && isAdmin && value === 8 && !self.isExternalOrder(this);
          }
        },
        {
          title: 'Exception Notes',
          var: 'order.exception',
          type: 'textarea',
          placeholder: 'Missing product, missing paperwork, store closed, weather, etc...',
          styles: `
            clear: both;
          `,
          options: {
            preserve: true,
            rules: [
              {
                required: true,
                message: 'Exception notes are required',
              }
            ]
          },
          canDisplay: function () {
            const { isEdit } = self.state;
            const { form: { getFieldValue }, sState } = self.props;
            const isAdmin = userHasRole([0, 1, 2], sState);
            const value = getFieldValue('order.status');
  
            return isEdit && isAdmin && value === OrderStatus.exception && !self.isExternalOrder(this);
          }
        },
        {
          title: 'Pickup Date',
          var: 'order.pickupDate',
          type: 'datepicker',
          canDisplay: function () {
            const { isEdit } = self.state;
            const { sState } = self.props;
            const isAdmin = userHasRole([0, 1, 2], sState);
            const value = self.getFieldValue(this, 'order.orderType');
            const isRejected = self.getFieldValue(this, 'order.status') === OrderStatus.rejected;
            const isDriverOrder = get(self, 'state.isDriverOrder');

            if (self.state.cashReturn && value !== 'cash_pickup') { return false }

            if (self.isAlwaysShow('order.pickupDate', this) && (value === 'layover' || value === 'cash_return' || value === 'cash_pickup')) { return true; }

            return (isDriverOrder ? true : !isRejected) && isEdit && isAdmin && (value === 'layover' || value === 'cash_return' || value === 'cash_pickup') && !self.isExternalOrder(this);
          },
          inputProps: (item) => ({
            // Card #254 — "hidden or disabled throughout the application": the
            // admin/driver date fields honour the blackout too, not just the two
            // customer-facing preferred dates.
            disabledDate: (date) => self.blackoutDisabled(date),
            onChange: (date) => {
              const { form: { setFieldsValue } } = this.props;
              setFieldsValue({ ['order.pickupDate']: getMidDate(date) })
            },
          }),
          options: {
            preserve: true,
          }
        },
        {
          title: 'Pickup Type',
          var: 'order.pickupType',
          type: 'dropdown',
          canDisplay: function () {
            const { sState } = self.props;
            const canView = self.state.isEdit && userHasRole([0, 1], sState);
            const value = self.getFieldValue(this, 'order.orderType');
            const isRejected = self.getFieldValue(this, 'order.status') === OrderStatus.rejected;

            if (self.isAlwaysShow('order.pickupType', this) && !isRejected && !self.state.cashReturn && (value !== 'dropoff')) { return true; }
  
            return !isRejected && !self.state.cashReturn && canView && value !== 'dropoff' && !self.isExternalOrder(this);
          },
          opts: () => {
            return [
              { title: 'Layover Pickup', value: 'layoverpickup' },
              { title: 'Warehouse', value: 'warehouse' },
              { title: 'Same day pickups', value: 'regular' },
            ]
          },
          options: {
            initialValue: 'regular',
            validateTrigger: 'onBlur',
          },
        },
        {
          title: 'Pickup Route',
          var: 'order.pickupRoute',
          type: 'routesearch',
          zip: function () {
            const v = self.getFieldValue(this, 'order.pickupLN');
            const lic = getRawLicense(v);
            return get(lic, 'Zip');
          },
          date: function (ths) {
            return self.getFieldValue(ths, 'order.pickupDate');
          },
          showAll: () => get(this.state, 'showAllRoutesPickup'),
          extraOption: () => <Checkbox style={{ position: 'absolute', top: -28, left: 98, width: 300 }} checked={get(this, 'state.showAllRoutesPickup')} onChange={e => this.handleAllOptionsChange(e, 'showAllRoutesPickup')}>Allow All Routes</Checkbox>,
          styles: `
            clear: both;
          `,
          canDisplay: function () {
            const { isEdit } = self.state;
            const { sState } = self.props;
            const isAdmin = userHasRole([0, 1], sState);
            const value = self.getFieldValue(this, 'order.orderType');
            const isRejected = self.getFieldValue(this, 'order.status') === OrderStatus.rejected;
            const isNewAdmin = !!get(self, 'state.isNewAdminOrder');

            if (self.state.cashReturn && value !== 'cash_pickup') { return false }

            if (self.isAlwaysShow('order.pickupRoute', this) && (value === 'layover' || value === 'cash_return' || value === 'cash_pickup')) { return true; }

            return !isRejected && (isEdit || isNewAdmin) && isAdmin && (value === 'layover' || value === 'cash_return' || value === 'cash_pickup') && !self.isExternalOrder(this);
          },
          onChange: function () {
            const vr = self.getFieldValue(this, 'order.driver.pickup', { returnVar: true });

            get(self, `${vr}.setValue`, () => 0)(undefined);
          }
        },
        {
          title: 'Pickup Route Driver',
          var: 'order.driver.pickup',
          type: 'driversearch',
          styles: `clear: both;`,
          route: function () { return self.getFieldValue(this, 'order.pickupRoute') },
          date: function () { return self.getFieldValue(this, 'order.pickupDate') },
          canDisplay: function () {
            const { isEdit } = self.state;
            const { sState } = self.props;
            const isAdmin = userHasRole([0, 1], sState);
            const value = self.getFieldValue(this, 'order.orderType');
            const isRejected = self.getFieldValue(this, 'order.status') === OrderStatus.rejected;
            const isNewAdmin = !!get(self, 'state.isNewAdminOrder');

            if (self.state.cashReturn && value !== 'cash_pickup') { return false }

            if (self.isAlwaysShow('order.driver.pickup', this) && (value === 'layover' || value === 'cash_return' || value === 'cash_pickup')) { return true; }

            return !isRejected && (isEdit || isNewAdmin) && isAdmin && (value === 'layover' || value === 'cash_return' || value === 'cash_pickup') && !self.isExternalOrder(this);
          }
        },
        {
          title: 'Drop Off Date',
          var: 'order.dropoffDate',
          type: 'datepicker',
          canDisplay: function () {
            const { sState } = self.props;
            const canView = self.state.isEdit && userHasRole([0, 1], sState);
            const value = self.getFieldValue(this, 'order.orderType');

            if (self.isAlwaysShow('order.dropoffDate', this) && (value === 'dropoff')) { return true; }
  
            return canView && value === 'dropoff' && !self.isExternalOrder(this);
          },
          inputProps: (item) => ({
            // Card #254 — see the Pickup Date field above.
            disabledDate: (date) => self.blackoutDisabled(date),
            onChange: (date) => {
              const { form: { setFieldsValue } } = this.props;
              setFieldsValue({ ['order.dropoffDate']: getMidDate(date) })
            },
          }),
        },
        {
          title: function () {
            const isRejected = self.getFieldValue(this, 'order.status') === OrderStatus.rejected;
            const isBulk = this.var.includes('__');

            return !isBulk && (get(self, 'state.cashReturn') || isRejected) ? 'Return Date' : 'Delivery Date';
          },
          var: 'order.deliveryDate',
          type: 'datepicker',
          canDisplay: function () {
            if (self.getFieldValue(this, 'order.customerPickup')) { return false; }
            if (self.isAlwaysShow('order.deliveryDate', this)) { return true; }
            const orderType = self.getFieldValue(this, 'order.orderType');
            if (orderType === 'cash_pickup') { return false; }
            return self.state.isEdit && userHasRole([0, 1, 2], self.props.sState) && !self.isExternalOrder(this)
          },
          inputProps: () => ({
            // Card #254 — see the Pickup Date field above.
            disabledDate: (date) => self.blackoutDisabled(date),
            onChange: (date) => {
              const { form: { setFieldsValue } } = this.props;
              setFieldsValue({ ['order.deliveryDate']: getMidDate(date) })
            },
          }),
        },
        {
          title: function () {
            const isRejected = self.getFieldValue(this, 'order.status') === OrderStatus.rejected;
            const isBulk = this.var.includes('__');

            return !isBulk && (get(self, 'state.cashReturn') || isRejected) ? 'Return Route' : 'Delivery Route';
          },
          extraOption: () => <Checkbox style={{ position: 'absolute', top: -28, left: 98, width: 300 }} checked={get(this, 'state.showAllRoutes')} onChange={this.handleAllOptionsChange}>Allow All Routes</Checkbox>,
          var: 'order.route',
          type: 'routesearch',
          zip: function () {
            const key = self.getFieldValue(this, 'order.destinationLN');
            const lic = getRawLicense(key);
            return get(lic, 'Zip');
          },
          date: function (ths) {
            return self.getFieldValue(ths, 'order.deliveryDate');
          },
          showAll: () => get(this.state, 'showAllRoutes'),
          styles: `
            clear: both;
          `,
          canDisplay: function () {
            if (self.getFieldValue(this, 'order.customerPickup')) { return false; }
            if (self.isAlwaysShow('order.route', this)) { return true; }
            const orderType = self.getFieldValue(this, 'order.orderType');
            if (orderType === 'cash_pickup') { return false; }
            return self.state.isEdit && userHasRole([0, 1], self.props.sState) && !self.isExternalOrder(this);
          },
          onChange: function () {
            const vr = self.getFieldValue(this, 'order.driver.delivery', { returnVar: true });

            get(self, `${vr}.setValue`, () => 0)(undefined);
          }
        },
        {
          title: function () {
            const isRejected = self.getFieldValue(this, 'order.status') === OrderStatus.rejected;
            const isBulk = this.var.includes('__');

            return !isBulk && (get(self, 'state.cashReturn') || isRejected) ? 'Return Route Driver' : 'Delivery Route Driver';
          },
          var: 'order.driver.delivery',
          type: 'driversearch',
          styles: `clear: both;`,
          route: function () { return self.getFieldValue(this, 'order.route') },
          date: function () { return self.getFieldValue(this, 'order.deliveryDate') },
          canDisplay: function () {
            if (self.getFieldValue(this, 'order.customerPickup')) { return false; }
            if (self.isAlwaysShow('order.driver.delivery', this)) { return true; }
            const orderType = self.getFieldValue(this, 'order.orderType');
            if (orderType === 'cash_pickup') { return false; }
            return self.state.isEdit && userHasRole([0, 1], self.props.sState) && !self.isExternalOrder(this)
          },
        },
        {
          title: () => 'Billing Notes',
          var: 'order.billingNotes',
          type: 'textarea',
          styles: `clear: both;`,
          canDisplay: function () {
            if (self.isAlwaysShow('order.billingNotes', this)) { return true; }
            return self.state.isEdit && userHasRole([0, 1], self.props.sState) && !self.isExternalOrder(this)
          },
        },
        {
          title: 'Dropoff Driver Signature',
          var: 'order.signature.dropoff',
          type: 'sigpad',
          canDisplay: function () {
            const value = self.getFieldValue(this, 'order.orderType');

            return getConfig('sigs.show') && value === 'dropoff' && !self.isExternalOrder(this);
          },
          props: () => ({
            ...get(this, 'state.order.data.order.signature.dropoff', {}),
            manifest: this.getManifestProps(),
            onSave: res => {
              const k = 'data.order.signature.dropoff';
              const o = { ...this.state.order };
              const result = { ...res, date: new Date().toISOString() };
              set(o, k, result);
              this.setState({ order: o });
            }
          })
        },
        {
          title: 'Pickup Driver Signature',
          var: 'order.signature.pickup',
          type: 'sigpad',
          canDisplay: function () {
            const { sState } = self.props;
            const isAdmin = userHasRole([0, 1, 2], sState);
            const value = self.getFieldValue(this, 'order.status');

            return getConfig('sigs.show') && isAdmin && [OrderStatus.orderPickedUp].includes(value) && !self.isExternalOrder(this);
          },
          props: () => ({
            ...get(this, 'state.order.data.order.signature.pickup', {}),
            manifest: this.getManifestProps(),
            onSave: res => {
              const k = 'data.order.signature.pickup';
              const o = { ...this.state.order };
              const result = { ...res, date: new Date().toISOString() };
              set(o, k, result);
              this.setState({ order: o });
            }
          })
        },
        {
          title: 'Driver Signature',
          var: 'order.signature.driver',
          type: 'sigpad',
          canDisplay: function () {
            const { sState } = self.props;
            const isAdmin = userHasRole([0, 1, 2], sState);
            const value = self.getFieldValue(this, 'order.status')

            return getConfig('sigs.show') && isAdmin && SHOULDSIGN.includes(value) && !self.isExternalOrder(this);
          },
          props: () => ({
            ...get(this, 'state.order.data.order.signature.driver', {}),
            manifest: this.getManifestProps(),
            onSave: res => {
              const k = 'data.order.signature.driver';
              const o = { ...this.state.order };
              const result = { ...res, date: new Date().toISOString() };
              set(o, k, result);
              this.setState({ order: o });
            }
          })
        },
        {
          title: 'Customer Signature',
          var: 'order.signature.customer',
          type: 'sigpad',
          canDisplay: function () {
            const value = self.getFieldValue(this, 'order.status') || get(self, 'state.order.data.order.status');

            return getConfig('sigs.show') && (SHOULDSIGN.includes(value) || self.state.cashReturn) && !self.isExternalOrder(this);
          },
          props: () => ({
            ...get(this, 'state.order.data.order.signature.customer', { emails: get(getRawLicense(get(this, 'state.order.data.destCompany.License #')), 'emails', '') }),
            disabled: !userHasRole([0, 1, 2], this.props.sState),
            manifest: this.getManifestProps(),
            showEmails: true,
            disclaimerType: 'customer',
            showTags: this.props.form.getFieldValue('order.status') === OrderStatus.partialrejection,
            onSave: res => {
              const k = 'data.order.signature.customer';
              const o = { ...this.state.order };
              const result = { ...res, date: new Date().toISOString() };
              set(o, k, result);
              this.setState({ order: o });
            }
          })
        }
      ],
      bulkOrders: [],
    };
  }
  setFieldValue = (ths, key, val) => {
    const { form } = this.props;

    const thsVar = get(ths, 'var');
    let v = key || thsVar;

    //is in bulkorders mode
    if (thsVar.includes('__')) {
      const [k, num] = thsVar.split('__');
      v = key && !key.includes('__') ? `${key}__${num}` : v;
      return form.setFieldsValue({ [v]: val });
    }

    return form.setFieldsValue({ [v]: val });
  }
  getFieldValue = (ths, key, opts = {}) => {
    opts = {
      returnVar: false,
      ...opts,
    }

    const { form } = this.props;

    const thsVar = get(ths, 'var');
    let v = key || thsVar;

    //is in bulkorders mode
    if (thsVar.includes('__')) {
      const [k, num] = thsVar.split('__');
      v = key && !key.includes('__') ? `${key}__${num}` : v;
      if (opts.returnVar) { return v }
      return form.getFieldValue(v);
    }

    if (opts.returnVar) { return v; }

    return form.getFieldValue(v);
  }
  isAlwaysShow = (key, ths) => {
    const isInternal = !this.isExternalOrder(ths);
    if (isInternal) {
      return getConfig(`orders.internal.alwaysShow`, []).includes(key);
    }
    return false;
  }
  getManifestProps = () => {
    return orderHasManifest(this.state.order).file ? getManifestUrl(orderHasManifest(this.state.order).file) : null;
  }
  filterBasedOnRoles = items => {
    const { sState } = this.props;
    const isDriver = userHasRole([2], sState);

    //Billing invoice, manifest, method, amount, delivery status for driver role.

    const isCashReturn = get(this, 'state.cashReturn');

    const driverItems = isCashReturn ? [
      'order.orderType',
      'order.pickupLN',
      'order.destinationLN',
      'order.status',
      'order.pickupDate',
      'order.deliveryDate',
      'billing.invoice',
      'billing.amount',
      'billing.methodSelect',
      'billing.methodSelectId',
      'order.signature.pickup',
      'order.signature.driver',
      'order.signature.customer',
    ] : [
      'order.pickupLN',
      'order.destinationLN',
      'order.status', 
      'order.rejected',
      'order.rejectedText',
      'order.exception',
      'billing.invoice', 
      'billing.amount', 
      'billing.methodSelect',
      'billing.methodSelectId',
      'billing.manifest', 
      'order.notes',
      'order.signature.pickup',
      'order.signature.driver',
      'order.signature.customer',
    ];

    if (isDriver) {
      return orderBy(items.filter(item => {
        const showIndex = driverItems.indexOf(item.var);

        if (showIndex > -1) {
          item.order = showIndex;
          return true;
        }
      }), 'order');
    }
    
    return items;
  }
  getOrderDef = ths => {
    const { form } = this.props;

    const v = get(ths, 'var');

    //is in bulkorders mode
    if (v && v.includes('__')) {
      const [k, num] = v.split('__');
      return form.getFieldValue(`order.orderDef__${num}`);
    }

    return form.getFieldValue('order.orderDef');
  }
  isExternalOrder = (ths) => {
    return this.getOrderDef(ths) == 'ext';
  }
  onNM = (val, dispItem) => {
    const { toDisplay } = this.state;

    const has = (val || '').toLowerCase().indexOf('nm-') > -1;

    const index = toDisplay.findIndex(fi => fi.var === dispItem.var);
    const _var = get(dispItem, 'nmProps.var');
    const existIndex = toDisplay.findIndex(f => f.var === _var);
    const alreadyExists = existIndex != -1;

    if (alreadyExists && has) { return }

    if (alreadyExists && !has) {
      toDisplay.splice(existIndex, 1);
    } else {
      toDisplay.splice(index + 1, 0, dispItem.nmProps)
    }

    this.setState({ toDisplay: [...toDisplay] });
  }
  isAllAvail = () => {
    return !!get(this, 'props.state.allAvail', get(this, 'props.history.location.state.allAvail'));
  }
  handleAllOptionsChange = (e, sel = 'showAllRoutes') => {
    const showAllRoutes = e.target.checked;

    this.setState({ 
      [sel]: showAllRoutes, 
    });
  }
  getValidRoutesForZip = (item) => {
    let sel = 'order.destinationLN';
    if (item.var.indexOf('__') > -1) {
      const [f, ind] = item.var.split('__');
      sel = `order.destinationLN__${ind}`;
    }
    const { Zip } = get(this, `${sel}.getSelected`, () => ({}))();
    if (!Zip) {
      return [];
    }

    const validRoutes = [];
    get(this, 'state.allRoutes', []).map(r => {
      get(r, 'data.rows', []).map(rr => {
        if (rr.zipCode === Zip) {
          validRoutes.push(rr);
        }
      })
    })

    return validRoutes;
  }
  // Card #254 — Holiday & Blackout Dates.
  //
  // HARD BLOCK vs SOFT OVERRIDE. antd greys out a `disabledDate` cell and makes
  // it physically unclickable, so there is no way to "confirm past" one. The two
  // required behaviours therefore use the two mechanisms this file already has:
  //
  //   roles 2/3/4 (driver, account admin, standard user) — HARD blocked, the
  //     blackout is composed into `disabledDate` so the cell is greyed out;
  //   roles 0/1 (super admin, admin) — the cell stays SELECTABLE and they are
  //     stopped at submit by canContinueFromDateCheck's window.confirm, matching
  //     the existing "admin may override a date rule" precedent in that function.
  //
  // Reads the calendar synchronously from the window cache (disabledDate runs
  // during render and cannot await). An unloaded or failed fetch yields an empty
  // calendar, which blocks nothing — this fails OPEN on purpose.
  blackoutFor = (date) => describeBlackoutForMoment(date, getBlackoutConfig());

  canOverrideBlackout = () => userHasRole([0, 1], this.props.sState);

  // For use inside `disabledDate` only. Always false for admins, so their cells
  // stay clickable and the override lives at the submit gate instead.
  blackoutDisabled = (date) => {
    if (this.canOverrideBlackout()) { return false; }
    return !!this.blackoutFor(date);
  }
  disabledDates = (date, item) => {
    // Card #280 round 2 — the admin Create Order modals (allAvail) offer every
    // day. The route-day rule itself is routeDayDisabled, which the admin
    // confirm asks directly: customers never get those modals.
    if (this.isAllAvail()) { return false; }
    return this.routeDayDisabled(date, item);
  }
  // Today and earlier, and every day the destination's routes don't run.
  // Card #280 round 3 — except today on a Same Day order inside the Auto
  // Approval window (sameDayTodayOpen): the window then decides whether
  // customers may pick it.
  routeDayDisabled = (date, item) => {
    const dayOpts = get(ROUTECOLS().find(f => f.dataIndex === 'dayOfTheWeek'), 'options', []);
    const validRoutes = this.getValidRoutesForZip(item);
    const allowedDays = flatten(validRoutes.map(vr => get(vr, 'dayOfTheWeek')));
    const isdatearr = DateTool.isDateArray(allowedDays);
    const allowedDayInts = size(dayOpts) ? allowedDays.map(d => get(dayOpts.find(opt => opt.value === d), 'val')) : [];

    const thisDay = date.day();
    const day = moment(date).startOf('day');
    const isBefore = day.isBefore(moment().startOf('day')) || (day.isSame(moment(), 'day') && !this.sameDayTodayOpen(item));
    return isBefore || (isdatearr ? !DateTool.dateInArr(date, allowedDays) : allowedDayInts.indexOf(thisDay) === -1);
  }
  // Card #280 round 2 — Auto Approval lead time / cutoff window: the earliest
  // pickup is today + lead days (+1 once the type's cutoff hour has passed,
  // unless that type's Lead Time Bypass is on) and the earliest delivery is
  // the day after it (Same Day: the same day, round 3). The rule itself is
  // app/src/actions/autoapprovaldates.js.
  //
  // Only while Auto-Approve New Orders and that order type's switch are on,
  // only on NEW orders (the same test the save path uses to pick createOrder
  // over updateOrder), and never on Internal-definition orders, which the form
  // creates already approved. Roles 2/3/4 get the days greyed out; roles 0/1
  // keep every day clickable and are asked "Continue anyway?" at submit
  // (canContinueFromDateCheck), like the blackout override above. Nothing is
  // hard-blocked: a day inside the window that arrives another way (bulk
  // upload, External Orders API) is simply not auto-approved.
  isNewOrderEntry = () => !(this.state.isEdit && !this.state.isNewAdminOrder && !this.state.isDriverOrder);
  autoApprovalWindowFor = (orderType, orderDef) => {
    if (!this.isNewOrderEntry() || orderDef === 'int') { return null; }
    return autoApprovalDates.autoApprovalWindow(orderType, getConfig);
  }
  // Card #280 round 3 — a Same Day order is picked up and delivered on its
  // delivery day, so while the window applies TODAY is a real Same Day
  // delivery date for every role: routeDayDisabled stops greying it, and the
  // window (cutoff hour, lead days, bypass) decides whether customers may pick
  // it. With no window (Auto-Approve or the Same Day switch off, an edit, an
  // Internal order) today stays greyed as it always has.
  sameDayTodayOpen = (item) => {
    const orderType = this.getFieldValue(item, 'order.orderType');
    if (orderType !== 'sameDay') { return false; }
    return !!this.autoApprovalWindowFor(orderType, this.getFieldValue(item, 'order.orderDef'));
  }
  // For use inside `disabledDate`; `field` is 'preferredPickup' or
  // 'preferredDelivery'. Always false for admins, unless `asCustomer` (the
  // admin confirm asking what a customer's calendar offers).
  autoApprovalDayDisabled = (date, item, field, asCustomer = false) => {
    if (!asCustomer && userHasRole([0, 1], this.props.sState)) { return false; }
    const win = this.autoApprovalWindowFor(
      this.getFieldValue(item, 'order.orderType'),
      this.getFieldValue(item, 'order.orderDef'),
    );
    if (!win) { return false; }
    return autoApprovalDates.isBeforeDay(date, field === 'preferredPickup' ? win.offeredPickup : win.offeredDelivery);
  }
  // The Preferred Delivery picker's rule for a non-Internal order: route days,
  // the blackout and the window. `asCustomer` drops the roles 0/1 exemptions
  // and the allAvail escape, i.e. it is the calendar a customer gets.
  preferredDeliveryDisabled = (date, item, asCustomer = false) => (
    (asCustomer ? this.routeDayDisabled(date, item) : this.disabledDates(date, item))
    || (asCustomer ? !!this.blackoutFor(date) : this.blackoutDisabled(date))
    || this.autoApprovalDayDisabled(date, item, 'preferredDelivery', asCustomer)
  )
  // The first day from `from` ('YYYY-MM-DD') on that `isDisabled` leaves open,
  // as 'YYYY-MM-DD', or null if none is open within a year (e.g. no
  // destination picked yet, so no route day matches).
  firstOpenDay = (from, isDisabled) => {
    for (let i = 0; i < 366; i++) {
      const day = autoApprovalDates.addDays(from, i);
      if (!isDisabled(getMidDate(day))) { return day; }
    }
    return null;
  }
  // 'Preferred Delivery Date (order 3) — Wed, Oct 7, 2026 is inside the Auto
  // Approval lead time / cutoff: customers can pick Mon, Oct 12, 2026 or later.'
  // The day named is the first one a customer's calendar leaves open, so route
  // days and blackouts count as well as the window. When there is none (no
  // destination or route yet) the sentence ends "customers cannot pick it."
  // rather than naming a wrong day.
  // For the admin confirm at submit. `order` is the de-suffixed order values,
  // `rowKey` the bulk spreadsheet row (undefined on the single-order form).
  autoApprovalWindowSentences = (order, rowKey) => {
    const win = this.autoApprovalWindowFor(get(order, 'orderType'), get(order, 'orderDef'));
    if (!win) { return []; }
    const suffix = rowKey === undefined || rowKey === null ? '' : `__${rowKey}`;
    const which = suffix ? ` (order ${Number(rowKey) + 1})` : '';
    const pretty = day => moment(day).format('ddd, MMM D, YYYY');
    const sentences = [];
    const check = (value, label, earliest, customerDisabled) => {
      if (!autoApprovalDates.isBeforeDay(value, earliest)) { return; }
      const day = autoApprovalDates.calendarDay(value);
      const first = this.firstOpenDay(win.today, customerDisabled);
      sentences.push(`${label}${which} — ${pretty(day)} is inside the Auto Approval lead time / cutoff: ${first ? `customers can pick ${pretty(first)} or later` : 'customers cannot pick it'}.`);
    };
    // Same Day has no pickup field; it is picked up on its delivery day, so
    // the delivery check covers it.
    if (win.orderType !== 'sameDay') {
      const pickupItem = { var: `order.preferredPickup${suffix}` };
      // The Preferred Pickup/Dropoff picker's customer rules: the window, past
      // days and the blackout. That picker's disabledDate is inline, so they
      // are listed here; it has no route-day rule.
      check(get(order, 'preferredPickup'), win.orderType === 'dropoff' ? 'Preferred Dropoff Date' : 'Preferred Pickup Date', win.offeredPickup, d => (
        this.autoApprovalDayDisabled(d, pickupItem, 'preferredPickup', true)
        || moment(d).startOf('day').isBefore(moment().startOf('day'))
        || !!this.blackoutFor(d)
      ));
    }
    const deliveryItem = { var: `order.preferredDelivery${suffix}` };
    check(get(order, 'preferredDelivery'), 'Preferred Delivery Date', win.offeredDelivery, d => this.preferredDeliveryDisabled(d, deliveryItem, true));
    return sentences;
  }
  // The admin confirm's closing words: what happens to THIS order. `order` is
  // the order the submit posts, so `status` is the one /orders/create gets:
  // Order Received from the New Order page, whatever the Delivery Status says
  // in the Create Order modal (preset Order Approved). The server keeps any
  // truthy status (`newStatus ? newStatus : ...` in getAutoApprovalValues);
  // only a falsy one is left to auto-approval, which inside the window means
  // Order Received.
  autoApprovalWindowOutcome = (order) => {
    const status = get(order, 'status');
    if (!status) {
      return 'an order inside the lead time or cutoff is left at Order Received for manual approval.';
    }
    const title = get(deliveryStatus.find(d => d.value === Number(status)), 'title');
    return `an order inside the lead time or cutoff is not auto-approved. This order will be created with the status selected in the form${title ? `: ${title}` : ''}.`;
  }
  handleSelectChange = (e, item) => {
    const {
      form: { setFields, setFieldsValue, getFieldValue, getFieldsValue },
    } = this.props;
    let errors = null;
    const isOrderType = item.var.indexOf('order.orderType') > -1;

    if (item.onSelect) { item.onSelect(e) }

    if (isOrderType && (e === 'layover' || e === 'sameDay' || e === 'cash_return' || e === 'cash_pickup' || e === 'rej_return')) {
      const isCash = e === 'cash_return' || e === 'cash_pickup' || e === 'rej_return';
      setTimeout(() => {
        setFieldsValue({ 'order.pickupType': e === 'sameDay' ? 'regular' : 'layoverpickup' });
        if (e === 'sameDay') {
          const value = getFieldValue('order.preferredDelivery');
          setFieldsValue({ 'order.deliveryDate': value });
          // var: 'order.preferredDelivery',
        }
        this.setState({ cashReturn: isCash ? true : undefined });
      }, 50);
    } else if (item.var.indexOf('.pickupLN') > -1 || item.var.indexOf('.destinationLN') > -1) {
      const fnames = (() => {
        let pickupLN = 'order.pickupLN';
        let destinationLN = 'order.destinationLN';
        if (item.var.indexOf('__') > -1) {
          const [order,ordernum] = item.var.split('__');
          pickupLN += `__${ordernum}`;
          destinationLN += `__${ordernum}`;
        }
        return {
          pickupLN,
          destinationLN,
        }
      })();

      const fields = getFieldsValue([fnames.pickupLN, fnames.destinationLN]);
      set(fields, item.var, e);
      
      const pln = get(fields, fnames.pickupLN);
      const dln = get(fields, fnames.destinationLN);
      setFieldsValue({
        [fnames.pickupLN]: pln,
        [fnames.destinationLN]: dln,
      })
      
      if (!pln || !dln) { return }

      const recLicenseRe = /^\d+R-/;
      const pHasR = recLicenseRe.test(pln);
      const dHasR = recLicenseRe.test(dln) || /^610/.test(dln);

      const useRCheck = get(window, `APPCONFIG.useRCheck`, true);

      if (useRCheck && pHasR !== dHasR) {
        errors = [new Error('Incorrect license type. Please review Medical or Recreational')];
        setTimeout(() => {
          setFields({
            [fnames.pickupLN]: {
              value: pln,
              errors
            },
          })
          get(this, [fnames.destinationLN, 'onChange'], () => 0)(dln);
          get(this, [fnames.destinationLN, 'selectOption'], () => 0)(dln);
        }, 200);
      }
    } else if (item.onChange) {
      item.onChange(e);
    }

    this.setState({});
    return errors;
  }
  componentWillUnmount = () => {
    this.Unmounted = true;
  }
  getAllRoutes = async () => {
    const orderDef = this.props.form.getFieldValue('order.orderDef');
    const allRoutes = await getRoutes({}, { useExternal: orderDef == 'ext' }).catch(() => []);
    this.setState({ allRoutes });
  }
  componentDidMount = async () => {
    this.getAllRoutes();
    // Card #254 — re-pull the blackout calendar every time the order form
    // mounts, so an admin's edit on the Holiday & Blackout Dates screen takes
    // effect without a page reload. The window cache is already primed at app
    // bootstrap; this only ever replaces it with a newer copy, and the setState
    // is what re-runs `disabledDate` against it.
    loadBlackoutDates()
      .then(() => { if (!this.Unmounted) { this.setState({ blackoutLoaded: true }); } })
      .catch(() => {});
    const state = get(this, 'props.state', get(this, 'props.history.location.state'));

    if (state && state.editOrder) {
      const eorder = state.editOrder;
      const serverOrder = eorder.id ? await getOrder(eorder.id) : eorder || {};
      const extraProps = { ...state, editOrder: undefined };

      const orderType = get(state, 'editOrder.data.order.orderType');
      const iscashReturn = !!state.cashReturn || get(state, 'editOrder.data.order.cashReturn') || orderType === 'cash_return' || orderType === 'cash_pickup' || orderType === 'rej_return';
      this.state.cashReturn = iscashReturn;
      this.state.order = serverOrder;
      this.state.isEdit = true;
      if (state.isNewAdminOrder) {
        this.state.isNewAdminOrder = state.isNewAdminOrder;
        const origLic = this.state.toDisplay.findIndex(td => td.var === 'pickupLN');
        set(this.state, `toDisplay.${origLic}.type`, 'lnsearch');
      }

      if (state.isDriverOrder) {
        this.state.isDriverOrder = true;
      }

      const order = get(serverOrder, 'data');
      const showAllRoutes = get(order, 'order.showAllRoutes', false);
      const showAllRoutesPickup = get(order, 'order.showAllRoutesPickup', false);
      
      this.state.showAllRoutes = showAllRoutes;
      this.state.showAllRoutesPickup = showAllRoutesPickup;

      this.setState({ ...this.state }, async () => {
        const ln = get(order, 'order.destinationLN');
        const oln = get(order, 'order.pickupLN');
        this.setFieldsOriginal(order, extraProps);

        const checkForAvail = async () => {
          return new Promise(async (resolve) => {
            const dln = get(this, 'order.destinationLN');
            const dState = get(dln, 'state.licenseNumbers', []);
            if (this.Unmounted) {
              resolve();
            } else if (dState.length > 0) {
              dln.onChange(ln);
              dln.selectOption(ln);
              this.setFieldsOriginal(order, extraProps);
              await wait(510);
              resolve();
            } else {
              await wait(500);
              await checkForAvail();
              resolve();
            }
          })
        }
        await wait(500);
        await checkForAvail();

        if (state.showCheckout) {
          setTimeout(() => {
            this.checkoutorder({ forceCheckout: true });
          }, 500);
        }

        if (this.state.isEdit && userHasRole([0, 1, 2], this.props.sState) && getConfig('sigs.show')) {
          this.setState({ notify: !get(order, 'emails.signed', false), intNotes: get(getRawLicense(ln), 'intNotes') });
        }

        setTimeout(() => {
          this.setState({ loading: false });
        }, 750)
      });
    } else {
      this.setState({ loading: false });
    }
  };
  // Card #266 — notify admins that a user added a license the system had not
  // seen before. LNSearch only calls this for a genuinely new number (it decides
  // that before writing the store), so this layer owns the message itself plus
  // the per-session de-duplication.
  notifyLicenseAdded = async (nl, o) => {
    // Defence in depth against a future auto-caller, mirroring the isAuto
    // precedent on the orderDef onChange handler above. AUTO_INVOKABLE_INPUT_PROPS
    // is the real guard; this makes the intent explicit at the handler itself.
    if (get(o, 'isAuto')) { return; }

    const licNum = licenseNumberOf(nl);

    if (!licNum || NOTIFIED_LICENSES.has(licNum)) { return; }

    // sendEmail is a guaranteed no-op in these two states. Claiming the number
    // here would suppress the notification for the rest of the session without
    // anything ever having been sent.
    if (this.state.cashReturn || this.isAllAvail()) { return; }

    // Claim up front so a burst cannot race past the check above — that is what
    // the de-duplication exists to stop — then give the number back if the send
    // actually fails, so a genuine retry can still notify.
    NOTIFIED_LICENSES.add(licNum);

    try {
      await this.sendEmail({
        subject: licenseAddedSubject(licNum),
        html: licenseAddedBody(nl, licNum, get(this.props.sState, 'user.email')),
        to: [...(getConfig('adminEmails', config().adminEmails))],
      });
    } catch (err) {
      NOTIFIED_LICENSES.delete(licNum);
      console.warn('New License Added notification failed', err);
    }
  }
  sendEmail = async email => {
    if (this.state.cashReturn) { return }
    if (!this.isAllAvail()) {
      if ((!email.to || email.notifyUsers) && email.order) {
        const ln = get(email, 'order.data.order.pickupLN');
        const users = await getUsersMatchingLn(ln);

        const conf = config();

        // Card #151 round 2 — narrow the license's users to Account Admins and
        // Users. The per-user opt-out still decides for THESE addresses (an
        // absent data.alerts.email means opted in, as it always has).
        const roleRecipients = users.filter(u => {
          const em = get(u, 'email');
          const allowed = get(u, 'data.alerts.email', true);
          const role = Number(get(u, 'data.role'));

          return !!em && !!allowed && CUSTOMER_ALERT_ROLES.includes(role);
        }).map(u => ({
          email: get(u, 'email'),
          label: ROLE_LABELS[Number(get(u, 'data.role'))],
        }));

        // The caller's configured list is UNIONed in rather than replaced: those
        // entries are shared operations boxes (orders@, warehouse@) set in Edit
        // Settings that are not user accounts, and they are the only recipients
        // left for an account that has no role 3/4 user. They carry no alerts
        // preference of their own, so the opt-out above must not touch them.
        const configuredRecipients = [
          ...toArray(email.to),
          ...(email.notifyAdmins ? toArray(getConfig('rejectionEmails', conf.rejectionEmails)) : []),
        ].map(e => ({ email: e, label: 'configured' }));

        const intended = dedupeRecipients([...roleRecipients, ...configuredRecipients]);

        // An account with no role 3/4 user and no configured list resolves to
        // nobody. Sending anyway reaches nodemailer with an empty recipient
        // list and rejects; there is simply nothing to deliver here.
        if (!intended.length) { return; }

        // Card #151 round 8 — THE STAGE LEAK. This was `isDev()` alone, and
        // isDev() matches localhost / gps-env-dev / dev.gpsorders.com only:
        // stage.gpsone.delivery is isStage(), a separate helper
        // (actions/utils.js). So on stage `intended` — the license's real
        // Account Admins, read straight out of the stage database — went into
        // `to` and were mailed. Danielle 2026-08-25: "Stage sent an email out to
        // a customer ... it sent it to one of the customer Profile emails."
        //
        // The authoritative fix is server side (server/src/actions/mailguard.js,
        // applied in the sendMail funnel) precisely because host sniffing in a
        // bundle is what failed here, and because emails sent from Profile,
        // the feedback button and the server's own controllers never reach this
        // function at all. This stays because the block it renders names each
        // recipient's ROLE, which the server cannot resolve.
        const guarded = isGuardedHost();
        const intendedEmails = intended.map(r => r.email);

        // Card #151 round 9 — the line below destroys `intended` before the POST,
        // so the server funnel guard (actions/mailguard.js) only ever saw the
        // redirect list and its banner, its refusal reason and its console log
        // all named the testers back to themselves instead of the people the mail
        // was for. Send the resolved list alongside the redirected `to` and the
        // server prefers it. DISPLAY ONLY on both sides: it is never a recipient,
        // and controllers/email.js deletes the key before nodemailer sees it.
        //
        // Only set on a guarded host: on a live site `to` already IS the intended
        // list, so the field would be pure noise on the wire.
        if (guarded) {
          email.intendedRecipients = intendedEmails;
        }

        email.to = guarded ? uniq(conf.devEmails) : intendedEmails;

        if (guarded) {
          // roleRecipients is the customer half of `intended`. When it is empty
          // the block would otherwise show only the configured admin boxes with
          // no explanation — which is exactly the report this round is answering.
          email.html = `${devRecipientBlock(intended, roleRecipients.length ? '' : noCustomerNote(ln, users))}${email.html || ''}`;
        }
      }
      await sendEmail(email);
    }
  }
  submit = async (e) => {
    stopProp(e);
    if (this.state.loading) {
      return;
    }

    if (userHasRole(2, this.props.sState)) {
      const isNewOrder = this.state.isDriverOrder || this.state.isNewAdminOrder;
      const cont = window.confirm(`Are you sure you want to ${isNewOrder ? 'create' : 'update'} this order?`);
      if (!cont) {
        return;
      }
    }

    this.state.loading = true;
    this.state.error = null;
    this.setState(this.state);

    const { form, sState } = this.props;

    //clear errors
    const resetObj = {};
    const vals = form.getFieldsValue();
    this.state.toDisplay.map(td => {
      const value = form.getFieldValue(td.var);
      set(resetObj, td.var, { value });
    })
    form.setFields(resetObj);

    form.validateFieldsAndScroll(async (err, _values) => {
      if (err) {
        const error = buildErrorMsgFromForm(err);
        return this.setState({ error, loading: false });
      }

      // Card #256 — COD payment enforcement (client guard). Mirrors the
      // authoritative server guard in orders/utils.js updateOrder (which also
      // covers the native WebView driver). Block completing a delivery when the
      // order is flagged Payment Required and no valid Cash/Check payment is
      // recorded, unless an admin override is applied.
      // Round 5 — "completing" is now delivered OR partial rejection
      // (PAYMENT_REQUIRED_STATUSES), and a Rejection Return is exempt entirely
      // so a child carrying a stale inherited flag can never become
      // uncompletable. Round 6 — Reconciliation Returns are exempt too
      // (colors.PAYMENT_EXEMPT_ORDER_TYPES); this MUST stay in lockstep with the
      // server guard, or an order whose payment fields are hidden could still be
      // blocked at the door with no way to satisfy it.
      // Round 7 — that lockstep now includes the master switch: with the feature
      // off this guard never fires, which matters most for an order flagged while
      // the feature was on. The checkbox is gone, so getFieldValue is undefined
      // and `paymentRequired` falls back to the stored flag — without the master
      // test below a legacy row would still raise the pink popup and be
      // impossible to complete.
      {
        const prFlag = form.getFieldValue('order.paymentRequired');
        const paymentRequired = prFlag === undefined
          ? !!get(this, 'state.order.data.order.paymentRequired')
          : !!prFlag;
        const newStatus = form.getFieldValue('order.status');
        const method = form.getFieldValue('billing.methodSelect');
        const ref = form.getFieldValue('billing.methodSelectId');
        const validCodPayment = (method === 'cash' || method === 'check') && !!(ref && String(ref).trim());
        const overrideApplied = !!form.getFieldValue('order.paymentOverrideEnabled')
          || !!get(this, 'state.order.data.order.paymentOverride');
        const isExemptReturn = this.isPaymentExemptReturn();
        if (paymentRequirementEnabled()
          && paymentRequired && !isExemptReturn && PAYMENT_REQUIRED_STATUSES.includes(newStatus) && !validCodPayment && !overrideApplied) {
          // Round 2 — the block must be VERY visible to the driver: raise the
          // big pink popup (codBlockModal). The inline Alert error is kept as a
          // harmless secondary surface once the popup is dismissed.
          return this.setState({
            error: COD_PAYMENT_ERROR,
            codBlockModal: true,
            loading: false,
          });
        }
      }

      //check dates
      // Card #284 — `rowKey` is the bulk spreadsheet row this call is for (the
      // `for (let key in orders)` index below), or undefined on the single-order
      // form. Needed because `vals` has already been de-suffixed into a normal
      // order shape while the FORM field is still named `...__<n>`, so setting an
      // inline error needs the suffix put back.
      const canContinueFromDateCheck = (vals, rowKey) => {
        //fix dates — normalize to noon local so saved values match the
        //schedule endpoint's exact-match query (which also uses getMidDate).
        //Must run for cash_return orders too, or they save at midnight local
        //and the schedule query misses them.
        [
          'order.preferredDelivery',
          'order.preferredPickup',
          'order.pickupDate',
          'order.dropoffDate',
          'order.deliveryDate'
        ].map(k => {
          const has = get(vals, k);

          if (!!has) {
            set(vals, k, getMidDate(has));
          }
        })

        if (this.state.cashReturn) { return true }

        const dayOpts = get(ROUTECOLS().find(f => f.dataIndex === 'dayOfTheWeek'), 'options', []);
        const validRoutes = get(this, 'state.routes', []).filter(r => get(jparse(r.value), 'id').indexOf('__utility__') === -1).map(r => ({ ...r, value: jparse(r.value) }));
        const allowedDays = validRoutes.map(vr => get(vr, 'value.day'));
        const allowedDayInts = allowedDays.map(d => get(dayOpts.find(opt => opt.value === d), 'val'));

        const prefDelSelector = 'order.preferredDelivery';
        const prefPickSelector = 'order.preferredPickup';
        const prefDeliveryDate = get(vals, prefDelSelector); //in moment
        const prefPickupDate = get(vals, prefPickSelector); //in moment

        const prefDay = prefDeliveryDate.day();

        if (!!prefPickupDate && prefPickupDate.startOf('day').isAfter(prefDeliveryDate.startOf('day'))) {
          form.setFields({
            [prefPickSelector]: {
              errors: [new Error('Pickup date cannot be after delivery date')]
            }
          })
          this.setState({ 
            loading: false, 
            error: `The preferred pickup date you selected cannot be after the preferred delivery date` 
          });
          return false;
        }

        if (size(allowedDayInts) && allowedDayInts.indexOf(prefDay) === -1) {
          //days dont' match fail
          form.setFields({
            [prefDelSelector]: {
              errors: [new Error('Day chosen not allowed')]
            }
          })
          this.setState({ 
            loading: false, 
            error: `The preferred delivery date you selected doesn't match a delivery route option. Please select one of the days listed: ${allowedDays.map(v => upperFirst(v)).join(', ')}` 
          });
          return false;
        }

        const isAdmin = userHasRole([0, 1], sState);

        // Card #254 — blackout dates. Card #284 — the scan now runs for EVERY
        // role, not just admins.
        //
        // On the single-order form roles 2/3/4 could never arrive here with a
        // blacked-out date: their calendar cells are composed with
        // `blackoutDisabled` and antd makes a disabled cell physically
        // unclickable, so the day was unreachable and this gate only had to
        // cover the admins whose cells stay live. The bulk spreadsheet importer
        // breaks that assumption — processData writes the date straight into the
        // field with setFieldsValue, and `disabledDate` is a RENDER-time
        // predicate for the picker popup, not a validator, so it never sees a
        // programmatic value. For a non-admin the hard block therefore did not
        // exist at all on the upload path.
        //
        // So the scan is shared and the roles part company only on what a hit
        // does:
        //
        //   roles 0/1 — the existing window.confirm soft override, unchanged,
        //     matching the route-day override precedent immediately below;
        //   roles 2/3/4 — HARD blocked with an inline field error, which is the
        //     upload-path equivalent of the greyed-out cell they get on the form.
        //
        // Scans every key under `order`. The bulk loop has already de-suffixed
        // `vals` into a normal order shape, which is why the row number comes
        // from `rowKey` rather than from the key itself.
        const blackoutCfg = getBlackoutConfig();
        const orderVals = get(vals, 'order', {}) || {};
        const savedOrder = get(this, 'state.order.data.order', {}) || {};
        const rowSuffix = rowKey === undefined || rowKey === null ? '' : `__${rowKey}`;
        const blackoutErrors = [];
        const blackoutFieldErrors = {};
        Object.keys(orderVals).forEach(k => {
          const field = blackoutDateField(k);
          if (!field) { return; }
          // "Existing scheduled orders should not be affected" — a date the
          // order ALREADY carries is never re-litigated. Editing an order that
          // predates the blackout (or was overridden once already) must not nag
          // on every later save; only a date being newly set is checked.
          if (toBlackoutDayString(savedOrder[k]) === toBlackoutDayString(orderVals[k])) { return; }
          const hit = describeBlackoutForMoment(orderVals[k], blackoutCfg);
          if (!hit) { return; }
          const day = toBlackoutDayString(orderVals[k]);
          const row = field.row !== null ? field.row : (rowSuffix ? Number(rowKey) : null);
          blackoutErrors.push(blackoutSentence({ label: field.label, row }, day, hit));
          blackoutFieldErrors[`order.${k}${rowSuffix}`] = {
            value: orderVals[k],
            errors: [new Error(`${moment(day).format('ddd, MMM D, YYYY')} is blacked out: ${hit.label}.`)],
          };
        });

        if (size(blackoutErrors)) {
          if (!isAdmin) {
            // Inline on the offending row's own date field AND in the page
            // Alert: on a 20-row upload the Alert is the only thing visible
            // without scrolling, and the field error is the only thing that says
            // WHICH row.
            form.setFields(blackoutFieldErrors);
            this.setState({
              loading: false,
              error: `${blackoutErrors.join(' ')} ${BLACKOUT_BLOCKED_MSG}`,
            });
            return false;
          }
          const proceed = window.confirm(
            `${blackoutErrors.join('\n')}\n\nBlacked-out days are not offered to customers. Schedule this order anyway?`
          );
          if (!proceed) { return false; }
        }

        // Card #280 round 2 — Auto Approval lead time / cutoff window. Roles
        // 2/3/4 cannot pick such a day (greyed out) and are deliberately not
        // blocked when one arrives another way (bulk upload): it is simply not
        // auto-approved. Admins keep every day clickable, so they confirm
        // here, in the same style as the blackout override above.
        if (isAdmin) {
          const windowSentences = this.autoApprovalWindowSentences(orderVals, rowKey);
          if (size(windowSentences)) {
            const proceed = window.confirm(
              `${windowSentences.join('\n')}\n\nThese days are not offered to customers, and ${this.autoApprovalWindowOutcome(orderVals)} Continue anyway?`
            );
            if (!proceed) { return false; }
          }
        }

        if (isAdmin) {
          const orderType = get(_values, 'order.orderType');
          const pickupDate = get(_values, 'order.pickupDate');
          const pickupRoute = get(_values, 'order.pickupRoute');
          const delRoute = get(_values, 'order.route');
          const delDate = get(_values, 'order.deliveryDate');

          const errors = [];

          if (pickupDate && pickupRoute) {
            const { day } = jparse(pickupRoute);
            const pickupRouteDay = get(dayOpts.find(d => d.value === day), 'val');
            if (pickupDate.day() !== pickupRouteDay) {
              errors.push(`The pickup date selected does not match the pickup route day.`);
            }
          }

          if (delDate && delRoute) {
            const { day } = jparse(delRoute);
            const delRouteDay = get(dayOpts.find(d => d.value === day), 'val');
            if (delDate.day() !== delRouteDay) {
              errors.push(`The delivery date selected does not match the delivery route day.`);
            }
          }

          if (size(errors)) {
            return window.confirm(errors.join('\n\n'));
          }
        }

        return true;
      }

      try {
        const { bulkOrders, showAllRoutes, showAllRoutesPickup } = this.state;

        const orders = {};
        const isBulk = bulkOrders.length > 0;
        if (isBulk) {
          bulkOrders.map((bo) => {
            if(bo.divider) { return 0 };

            const spl = bo.var.split('__');
            set(orders, `${spl[1]}.${spl[0]}`, get(_values, bo.var));
          })
        } else {
          orders['0'] = { ..._values };
        }

        let done = [];
        const orderErrors = [];
        const checkDone = () => {
          done.push(1);
          if (done.length >= Object.keys(orders).length) {
            const isNew = this.state.isDriverOrder || this.state.isNewAdminOrder;
            if (size(orderErrors)) {
              const etext = orderErrors.map(e => get(e, 'response.data.error', e.toString())).join('\n');
              // Card #256 round 2 — a server-side COD block (authoritative
              // guard in orders/utils.js, also covers the native WebView) must
              // surface as the big driver popup, not a small toast. Match with
              // includes(): etext can be several \n-joined order errors.
              if (etext.includes(COD_PAYMENT_ERROR)) {
                this.setState({ codBlockModal: true, error: COD_PAYMENT_ERROR, loading: false });
              } else {
                message.error(`Failed to save order(s): ${etext}`);
              }
            } else {
              message.success(`Order ${isNew ? 'created' : 'updated'} successfully!`);
              this.goBack(true);
            }
          }
        }
        
        const currentUser = get(sState, 'user', {});
        const ordersToProcess = [];
        
        for (let key in orders) {
          const dln = `order.destinationLN`;
          const val = orders[key];
          const sel = dln + (isBulk ? `__${key}` : '');
          const comp = get(this, sel);
          const destinationLN = get(val, dln, get(comp, 'state.selectedOption'));
          const status = get(val, 'order.status', get(this.state, 'order.data.order.status', deliveryStatus[0].value));
          const lnexists = get(this, 'LNSEARCH.state.licenseNumbersAll', []).find(l => get(l, 'License #') === destinationLN);
          // "Date Order Approved" must be a STABLE, write-once stamp. `val` is the
          // antd form delta and never carries `order.dateApproved`, so the old guard
          // was always falsy and every status advance (pickup/checkin/transit/
          // deliver) fell through to `moment()`, clobbering the existing stamp that
          // is spread in below — the Lifecycle Report's approval time drifted forward
          // to the delivery time (Joel/Danielle, card #11, 2026-06-17). Seed the guard
          // from the persisted stamp so it is preserved across later-stage saves. A
          // genuine re-approval — status transitioning INTO Order Approved from a
          // different prior status (e.g. after a rejection) — DOES re-stamp to the
          // new approval time.
          const prevStatus = get(this, 'state.order.data.order.status');
          const existingDateApproved = get(this, 'state.order.data.order.dateApproved');
          const isReApproval = Number(status) === OrderStatus.orderApproved
            && Number(prevStatus) !== OrderStatus.orderApproved;
          const hasDateApproved = get(val, 'order.dateApproved', existingDateApproved);
          const isDriver = userHasRole(2, this.props.sState);

          const values = {
            ...(get(this, 'state.order.data', {}) || {}),
            ...val,
            order: {
              ...get(this, 'state.order.data.order', {}),
              ...get(val, 'order', {}),
              weight: get(val, 'order.weight', get(this, 'state.order.data.order.weight')) || '1',
              status,
              dateApproved: isReApproval
                ? moment().toISOString()
                : hasDateApproved
                  ? hasDateApproved
                  : status >= 1 ? moment().toISOString() : undefined,
              destinationLN,
              showAllRoutes,
              showAllRoutesPickup,
              cashReturn: this.state.cashReturn,
            }
          }

          if (!isDriver && !canContinueFromDateCheck(values, isBulk ? key : null) || this.handleSelectChange(destinationLN, { var: sel })) {
            return this.setState({ loading: false });
          }

          //no destinationLN picked. Have to do this because of our custom AutoComplete Input on form
          const value = get(values, dln);
          const isNM = (value || '').toLowerCase().indexOf('nm-') > -1 && this.isAllAvail();
          if (!isDriver && !isNM && (!value || !lnexists)) {
            this.props.form.setFields({
              [sel]: {
                value,
                errors: [new Error(!value ? 'Destination is required!' : "License number doesn't exist")]
              }
            });
            setTimeout(() => {
              this.props.form.setFields({
                [sel]: {
                  value,
                  errors: [new Error(!value ? 'Destination is required!' : "License number doesn't exist")]
                }
              });
            }, 500);
            
            this.setState({ 
              loading: false,
              error: 'Please fix the errors below.'
            });
            break;
          }

          const destLN = get(values, 'order.destinationLN');
          const pickupLN = get(values, 'order.pickupLN');
          const licenses = get(this, 'LNSEARCH.state.licenseNumbersAll', []);
          const destL = licenses.find(l => get(l, ['License #']) === destLN);
          const pickupL = licenses.find(l => get(l, ['License #']) === pickupLN);

          const _order = {
            ...values,
            pickupCompany: {
              ...pickupL,
              // ...get(pickupUser, 'data.company', {}), //no longer going to include the company profile information in the order
              ...get(values, 'order.pickupCompany', {}),
            },
            destCompany: {
              ...destL,
              // ...get(destUser, 'data.company', {}), //no longer going to include the company profile information in the order
              ...get(values, 'order.destCompany', {}),
            },
          }

          if (get(_order, 'billing.methodSelect')) {
            const methodVal = get(_order, 'billing.methodSelect');
            if (methodVal === 'cash') {
              set(_order, 'billing.method', `Cash ${get(_order, 'billing.methodSelectId', '')}`);
            } else if (methodVal === 'check') {
              set(_order, 'billing.method', `Check ${get(_order, 'billing.methodSelectId', '')}`);
            } else if (methodVal === 'net') {
              set(_order, 'billing.method', `Net Terms`)
            } else if (methodVal === 'samples') {
              set(_order, 'billing.method', `Samples`);
            }
          }

          const sigs = get(this.state, 'order.data.order.signature');
          if (sigs) { set(_order, 'order.signature', sigs) }

          //Dont need logo in order
          unset(_order, 'pickupCompany.logo');
          unset(_order, 'destCompany.logo');

          // Customer Pickup returns are collected by the customer — strip the
          // return route/driver. Reconciliation Returns (cash_return) keep a
          // deliveryDate so the order still surfaces in the dashboard's
          // Reconciliation Returns container; default it to the order's
          // creation date (or today for new orders) per Joel 2026-05-21.
          // Rejection Returns (rej_return) continue to clear the date — they
          // surface in the Rejection Returns container by their own dropoff
          // record. Use null (not undefined) so it overrides any prior value
          // when the edited order is lodash-merged with its stored data.
          if (get(_order, 'order.customerPickup')) {
            const orderType = get(_order, 'order.orderType');
            if (orderType === 'cash_return') {
              const existingCreatedAt = get(this.state, 'order.data.createdAt')
                || get(this.state, 'order.data.dateSubmitted');
              const createdMoment = existingCreatedAt ? moment(existingCreatedAt) : moment();
              const fallbackDate = createdMoment.toISOString();
              if (!get(_order, 'order.deliveryDate')) {
                set(_order, 'order.deliveryDate', fallbackDate);
              }
            } else {
              set(_order, 'order.deliveryDate', null);
            }
            set(_order, 'order.route', null);
            set(_order, 'order.driver.delivery', null);
          }

          const order = new _Order(_order);

          // Card #256 — when an admin/super-admin toggles the COD override on,
          // stamp who/when/why onto data.order.paymentOverride so (a) the
          // enforcement guards allow completion and (b) the server writes an
          // operations_log audit row. Reason is optional.
          // Round 7 — master switch. The checkbox is hidden when the feature is
          // off, but `values` above spreads the SAVED data.order, so an order
          // overridden while the feature was on still carries
          // paymentOverrideEnabled: true and would re-stamp a fresh by/at on every
          // admin save — writing new Payment Override audit rows for a feature
          // that is switched off. This only skips the RE-stamp: the existing
          // data.order.paymentOverride is preserved by the merge below, so turning
          // the master back on restores today's behaviour exactly.
          if (paymentRequirementEnabled()
            && get(_order, 'order.paymentOverrideEnabled') && userHasRole([0, 1], sState)) {
            const overrideByName = `${get(currentUser, 'firstName', '')} ${get(currentUser, 'lastName', '')}`.trim()
              || get(currentUser, 'email', '');
            set(order, 'order.paymentOverride', {
              by: overrideByName,
              byId: currentUser.id,
              at: new Date().toISOString(),
              reason: get(_order, 'order.paymentOverrideReason', '') || '',
            });
          }

          order.createdBy = currentUser.id;
          order.updatedBy = currentUser.id;
          order.dateSubmitted = new Date().toISOString();
          order.deliveryDate = get(order, 'order.deliveryDate', order.deliveryDate);
          
          if (this.state.isEdit && !this.state.isNewAdminOrder && !this.state.isDriverOrder) { //can't be bulk, we are only editing a single order
            const oOrder = { ...this.state.order };
            order.createdAt = oOrder.data.createdAt;
            order.createdBy = oOrder.data.createdBy;
            order.dateSubmitted = oOrder.data.dateSubmitted;

            const merged = {
              ...oOrder,
              data: merge({}, order, {
                order: merge({}, get(oOrder, 'data.order', {}), get(order, 'order', {})),
              })
            }

            if (orderIsRejected(merged) && !get(merged, 'data.order.rejectedDate')) {
              set(merged, 'data.order.rejectedDate', new Date().toISOString());
            }

            this.state.isEdit ? await updateOrder(merged) : await createOrder(merged);

            // Card #151 round 4 — the Customer Delivery Confirmation is now
            // DECOUPLED from the manifest PDF. This used to be gated on
            // `hasFile`, so an order completed with a captured signature but no
            // uploaded manifest sent nothing at all and reported nothing — which
            // is exactly why three rounds passed on an environment where no
            // order has a manifest file. /signmanifest tolerates a missing
            // manifest (it skips the PDF signing step and mails the confirmation
            // with the Manifest column blank), so the preconditions are now just
            // a signature plus a completed status. Same endpoint, same request
            // shape. The `hasFile ||` half preserves the pre-existing PDF-signing
            // behaviour at any status.
            const { hasFile } = orderHasManifest(merged);
            const hasSignature = !!size(cleanCopy(get(merged, 'data.order.signature')));
            const isDeliveryStatus = DELIVERY_CONFIRMATION_STATUSES.includes(get(merged, 'data.order.status'));
            // Card #151 round 6 — a Rejection / Reconciliation Return never
            // emails the customer. The checkbox is hidden for these types (see
            // showNotify in render), but hiding it is NOT enough on its own:
            // `notify` is defaulted to true on load regardless of whether the
            // control renders, so the value has to be forced off here as well.
            // The signManifest CALL still runs — the manifest PDF must still be
            // signed/generated for a return that carries one; only the
            // notification is suppressed.
            const notify = this.state.notify
              && !isDeliveryConfirmationExcludedType(get(merged, 'data.order.orderType'));
            if (hasSignature && (hasFile || isDeliveryStatus)) {
              // updateOrder above has already committed the order, so a failure
              // in this step must NOT surface as "Order creation failed" — say
              // what actually failed and let the save report itself normally.
              const signResult = await signManifest({ order: merged, opts: { notify }})
                .catch(e => {
                  const reason = get(e, 'response.data.error') || e.message;
                  console.error('[signmanifest]', e);
                  message.error(`Order saved, but the manifest / delivery confirmation step failed: ${reason}`, 10);
                  return null;
                });
              // /signmanifest now returns emailSent / emailError / recipients
              // rather than swallowing the outcome. Surface BOTH results — a
              // silent failure here is the whole defect Danielle reported.
              // Round 6 — reads the SUPPRESSED value, so a return neither claims
              // a confirmation was sent nor warns that one failed: none was
              // asked for.
              if (signResult && notify) {
                if (get(signResult, 'emailSent')) {
                  const sentTo = toArray(get(signResult, 'recipients', [])).join(', ');
                  message.success(`Customer delivery confirmation sent${sentTo ? ` to ${sentTo}` : ''}.`, 6);
                } else {
                  const emailError = get(signResult, 'emailError') || 'Customer delivery confirmation was not sent.';
                  console.warn('[signmanifest]', emailError, signResult);
                  message.warning(emailError, 10);
                }
              }
            }

            const { isReconOrder, hasSig } = orderHasReconForm(merged);
            if (isReconOrder && hasSig) {
              // Round 6 — pass the SUPPRESSED value, not the raw state. This is
              // latent today because /signrecon's email block is commented out
              // server-side, but a Reconciliation Return is precisely the type
              // that comes through here, so re-enabling that block with the raw
              // state would resurrect the very emails this round removed — and
              // with no checkbox left on screen to turn them off.
              await signRecon({ order: merged, opts: { notify }});
            }

            const orderStatus = get(merged, 'data.order.status');
            const prefDelivery = moment(get(merged, 'data.order.preferredDelivery'));
            const delDate = moment(get(merged, 'data.order.deliveryDate'));
            const oDelDate = moment(get(oOrder, 'data.order.deliveryDate'));

            // Card #291 — the Destination row shared by the three customer
            // emails below (Order Update, Order Rejected, Order Partially
            // Rejected). `merged` first because it carries a destination changed
            // in THIS save, `oOrder` second because merged.data.destCompany is
            // rebuilt from the LNSearch license list and collapses to the form's
            // own `destCompany[Street Address]` when that list has not loaded —
            // the stored record is the only source that always has the DBA and
            // the number. Empty on both sides means the row is simply omitted.
            const destLine = destinationLine(merged) || destinationLine(oOrder);

            //Compare preferred dates at calendar-day granularity and normalize
            //both sides the same way (getMidDate) before comparing. Previously
            //this compared raw `.toISOString()` instants, so timezone/ms
            //round-tripping (the form value is run through getMidDate while the
            //original isn't) made the strings differ on every save and fired a
            //false "GPS Order date change" email even when the day was unchanged.
            const oPrefDel = get(oOrder, 'data.order.preferredDelivery');
            const cPrefDel = get(merged, 'data.order.preferredDelivery');
            const oPrefPick = get(oOrder, 'data.order.preferredPickup');
            const cPrefPick = get(merged, 'data.order.preferredPickup');

            const sameDay = (a, b) => {
              const ma = a ? getMidDate(a) : null;
              const mb = b ? getMidDate(b) : null;
              if (!ma && !mb) { return true }
              if (!ma || !mb) { return false }
              return ma.isSame(mb, 'day');
            };

            const delChanged = !sameDay(oPrefDel, cPrefDel);
            const pickChanged = !sameDay(oPrefPick, cPrefPick);

            if (delChanged || pickChanged) {
              const datechanges = [pickChanged ? { old: oPrefPick, new: cPrefPick, name: 'Pickup Date' } : null, delChanged ? { old: oPrefDel, new: cPrefDel, name: 'Delivery Date' } : null].filter(f => !!f)
                .map(dc => `${dc.name}: ${moment(dc.old).format('L')} -> ${moment(dc.new).format('L')}`).join('<br />');
              
              // Card #151 round 7 — Admin Order Date Change email (Danielle
              // 2026-08-25 mockup). Admin-only (configured adminEmails list, no
              // notifyUsers, no contact block). The legacy hardcoded 'GPS ' is
              // replaced by the tenant prefix, matching the Order Rejected /
              // Order update subjects in this same file; the order id moves out
              // of sentence 1 and into the new deep link line.
              //
              // Card #151 round 9 — Danielle 2026-08-28: "The Admin Order Date
              // Change email is working in PROD. The same email is not being
              // received in STAGE." The TRIGGER is deliberately untouched: this
              // fires only on a calendar-day change to a PREFERRED pickup or
              // delivery date (commit a433144 narrowed it to stop a false email
              // on every save) and the compiled predicate is byte-identical on
              // both tiers, so widening it would just add PROD volume. What was
              // genuinely broken is below: the call was neither awaited nor
              // caught, so an un-awaited rejection escaped the enclosing try —
              // no toast, and no log naming this send, only the browser's generic
              // "Uncaught (in promise)" line. That is why "no email" arrived with
              // "no error" and cost a whole test round.
              // Not awaited on purpose — awaiting would change when this fires
              // and would let a mail failure fail the order save itself. The
              // .catch reports to Bugsnag by hand because catching the rejection
              // is what removes it from the auto-capture it used to reach; see
              // the note above adminRecipients.
              this.sendEmail({
                order: oOrder,
                subject: [getConfig('emails.subjectPrefix'), `Order date change for (${oOrder.orderId})`].filter(f => !!f).join(' - '),
                html:
`An order date change has been made<br />
<a href="${config().appUrl}/order/${oOrder.id}"><b>View Order Here: (${oOrder.orderId})</b></a><br /><br />
${datechanges}`,
                to: [...adminRecipients('Order date change email')],
              }).catch(err => {
                console.warn('Order date change email failed to send', err);
                try { Bugsnag.notify(err); } catch (_e) { /* diagnostics must never break the app */ }
              })
            }

            if (get(oOrder, 'data.order.status') !== orderStatus && (orderStatus === OrderStatus.exception || orderStatus === OrderStatus.fullrejection || orderStatus === OrderStatus.partialrejection)) {
              const dStatus = deliveryStatus.find(d => d.value === order.order.status);
              
              try {
                const isPartial = orderStatus === OrderStatus.partialrejection;
                if (orderStatus === OrderStatus.fullrejection || isPartial) {
                  let rejection = get(Rejections.find(r => r.value === get(merged, 'data.order.rejected')), 'title', 'Other');

                  if (rejection === 'Other') {
                    const rejectedText = get(values, 'order.rejectedText');
                    rejection = !!rejectedText ? rejectedText : 'Other';
                  }

                  const hasTags = get(oOrder, 'data.order.signature.customer.tags');
                  const tags = isPartial && hasTags ? `<br /><br />Please see rejected tags: ${hasTags}` : '';

                  // Card #151: optional tenant subject prefix (emails.subjectPrefix);
                  // body per mock — order link after sentence 1, each sentence on its
                  // own line, contact info (orders.contactText) on its own line below.
                  // Round 2: the href is the /order/<guid> gateway deep link, which
                  // routes each recipient to the screen they can actually see and
                  // opens the order; a ?search= on Outgoing Orders dead-ends at
                  // "No Data" for anyone whose licenses don't include this order.
                  //
                  // Card #291 — Destination (DBA + License #) sits directly under
                  // the return sentence and above the contact block, which is
                  // where the annotated Order Partially Rejected mockup boxes it.
                  // When there is no destination to name the row collapses to
                  // nothing and the blank line before the contact block is
                  // unchanged (the stray newline is whitespace to a mail client).
                  this.sendEmail({
                    order: oOrder,
                    subject: [getConfig('emails.subjectPrefix'), `Order ${isPartial ? 'Partially ' :''}Rejected (${oOrder.orderId})`].filter(f => !!f).join(' - '),
                    html: `Your order was ${isPartial ? 'partially ' :''}rejected for ${rejection}.<br />
<a href="${config().appUrl}/order/${oOrder.id}"><b>View Order Here: (${oOrder.orderId})</b></a><br />
It will be returned back to you on our next pickup at your location.<br />
${destLine ? `${destLine}<br />` : ''}<br />
${getConfig('orders.contactText', 'For questions or more information, please contact us at <a href="mailto:orders@greenparcelservice.com">orders@greenparcelservice.com</a> or give us a call at 303-632-8772.')}${tags}`,
                    // notifyAdmins: true,
                    notifyUsers: true,
                    to: [...(getConfig('rejectionEmails', config().rejectionEmails))],
                    // Card #151 round 9 — the enclosing `try { } catch (_err)`
                    // never caught these: sendEmail is async and none of the
                    // three calls in this block is awaited, so a rejection
                    // escapes the try instead of landing in it. That is the same
                    // silent failure that hid the Date Change 500 above. A
                    // .catch keeps the call un-awaited (so WHEN it fires is
                    // unchanged, and a mail failure still cannot fail the order
                    // save) while making the failure visible. It also notifies
                    // Bugsnag by hand: consuming the rejection is exactly what
                    // stops @bugsnag/browser auto-capturing it on stage/prod.
                  }).catch(err => {
                    console.warn('Order Rejected email failed to send', err);
                    try { Bugsnag.notify(err); } catch (_e) { /* diagnostics must never break the app */ }
                  })
                } else if (orderStatus === OrderStatus.exception) {
                  // Card #151 round 7 — Admin Order Exception email (Danielle
                  // 2026-08-25 mockup). Admin-only: recipients stay the configured
                  // Exception list, no notifyUsers, and deliberately NO
                  // orders.contactText block — this never reaches a customer.
                  // Subject gains the tenant prefix (emails.subjectPrefix =
                  // "Company Name DBA"); body gains the /order/<guid> gateway deep
                  // link on its own line under sentence 1, exactly as the Order
                  // Rejected / Order update emails above.
                  this.sendEmail({
                    order: oOrder,
                    subject: [getConfig('emails.subjectPrefix'), `Order Exception for (${oOrder.orderId})`].filter(f => !!f).join(' - '),
                    html: `An Order Exception has been made<br />
<a href="${config().appUrl}/order/${oOrder.id}"><b>View Order Here: (${oOrder.orderId})</b></a><br /><br />
<b>Notes:</b><br />${get(values, 'order.exception')}`,
                    to: [...(getConfig('exceptionEmails', config().exceptionEmails))],
                    // Card #151 round 9 — see the Order Rejected send above.
                  }).catch(err => {
                    console.warn('Order Exception email failed to send', err);
                    try { Bugsnag.notify(err); } catch (_e) { /* diagnostics must never break the app */ }
                  })
                } else {
                  this.sendEmail({
                    order: oOrder,
                    // Card #151: optional tenant subject prefix (emails.subjectPrefix).
                    // Card #291 — Destination (DBA + License #) on its own line
                    // under the sentence, as on the other Order update email.
                    subject: [getConfig('emails.subjectPrefix'), `Order update (${oOrder.orderId})`].filter(f => !!f).join(' - '),
                    html: `Your order's status has recently been changed to ${dStatus.title}.${destLine ? `<br />
${destLine}` : ''}`,
                    // Card #151 round 9 — see the Order Rejected send above.
                  }).catch(err => {
                    console.warn('Order update (status) email failed to send', err);
                    try { Bugsnag.notify(err); } catch (_e) { /* diagnostics must never break the app */ }
                  })
                }
              } catch (_err) {
                console.warn(_err);
              }
            } else if (!prefDelivery.isSame(delDate, 'day') && !oDelDate.isSame(delDate, 'day')) {
              try {
                // Card #151: optional tenant subject prefix (emails.subjectPrefix);
                // body per mock — order link after sentence 1, new delivery date bold
                // on its own line, contact info (orders.contactText) on its own line.
                // Round 2: /order/<guid> gateway deep link (see the rejection email).
                // Card #291 — Destination (DBA + License #) under the new date and
                // above the contact block, the same placement as the rejection email.
                this.sendEmail({
                  order: oOrder,
                  subject: [getConfig('emails.subjectPrefix'), `Order update (${oOrder.orderId})`].filter(f => !!f).join(' - '),
                  html:
`Your order's preferred delivery date cannot be met.<br />
<a href="${config().appUrl}/order/${oOrder.id}"><b>View Order Here: (${oOrder.orderId})</b></a><br />
Your new delivery date is <b>${moment(delDate).format('L')}</b>.<br />
${destLine ? `${destLine}<br />` : ''}<br />
${getConfig('orders.contactText', 'For questions or more information, please contact us at <a href="mailto:orders@greenparcelservice.com">orders@greenparcelservice.com</a> or give us a call at 303-632-8772.')}`,
                  // Card #151 round 9 — see the Order Rejected send above; this
                  // try/catch could not catch an un-awaited rejection either.
                }).catch(err => {
                  console.warn('Order update (delivery date) email failed to send', err);
                  try { Bugsnag.notify(err); } catch (_e) { /* diagnostics must never break the app */ }
                })
              } catch (_err) {
                console.warn(_err);
              }
            }

            checkDone();
          } else {
            ordersToProcess.push({ 
              data: order,
              userId: currentUser.id,
              deliveryDate: order.deliveryDate,
            });
          }
        }

        if (ordersToProcess.length === Object.keys(orders).length) {
          await Promise.all(ordersToProcess.map(async (o) => {
            await createOrder(o).catch(e => {
              orderErrors.push(e);
            });
            const alertNewOrders = getConfig('alertNewOrders');
            if (alertNewOrders && !isLocal) {
              await this.sendEmail({
                order: o,
                subject: 'New Order submitted',
                html: `New order submitted: ${o.orderId}`,
                to: alertNewOrders,
              })
            }
            checkDone();
          }))
        }
      } catch (err) {
        console.error(err);
        // Card #256 round 2 — the edit/update path (`await updateOrder(merged)`)
        // throws straight to this catch, with the server guard's text on
        // err.response.data.error (err.message is only axios's generic 500
        // text). Match it here too so a server-side block on an existing order
        // raises the same driver popup.
        const codText = `${get(err, 'response.data.error', err.message || '')}`;
        if (codText.includes(COD_PAYMENT_ERROR)) {
          this.setState({ codBlockModal: true, error: COD_PAYMENT_ERROR, loading: false });
        } else {
          message.error('Order creation failed!');
          this.setState({
            loading: false,
            error: `Order creation failed: ${err.message}!`
          })
        }
      } finally {
        this.setState({ loading: false });
      }
    });
  };
  navigate = (cb, shouldUpdate) => {
    const { isModal, onClose } = this.props;
    if (isModal && onClose) {
      onClose(shouldUpdate);
    } else {
      cb();
    }
  }
  deleteOrder = () => {
    Modal.confirm({
      title: 'Delete Order?',
      content: 'Are you sure you want to delete this order?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        this.setState({ loading: true });
        await deleteOrder(this.state.order);
        this.navigate(this.props.history.goBack, true);
      }
    })
  }
  // Card #256 round 5 — single short-circuit for EVERY payment surface on this
  // screen. A Rejection Return inherits its parent's data.order wholesale, so
  // rows written before the server-side fix still carry paymentRequired plus the
  // two contact values; gating on order type suppresses the whole block (and the
  // completion guard) for those rows with no data migration. Reads the saved
  // order first and the live dropdown second, so it is correct both when editing
  // the auto-created child and while an Admin is changing the type by hand.
  // `item` is optional and only matters on the bulk-upload path, where every
  // toDisplay entry is cloned under a `__{i}` suffix.
  // Round 6 — widened to Reconciliation Returns (`cash_return`) as well
  // (Danielle 2026-08-03), and renamed to match: the round-5 name said
  // "rejection" and no longer described what it matches. Both halves read the
  // single client list, colors.PAYMENT_EXEMPT_ORDER_TYPES, which is the same
  // predicate that clears the orange Order ID and the "Payment Required" tag on
  // the Orders / Schedule lists — one rule, four surfaces. The list is
  // duplicated once more in server/src/controllers/orders/utils.js (same
  // constant name) because it cannot be shared across the wire; the two MUST
  // stay in lockstep, or the server rejects a completion this screen gave the
  // driver no way to satisfy.
  isPaymentExemptReturn = (item) => {
    if (colors.isPaymentExemptReturn(this.state.order)) { return true }
    const live = item
      ? this.getFieldValue(item, 'order.orderType')
      : this.props.form.getFieldValue('order.orderType');
    return colors.PAYMENT_EXEMPT_ORDER_TYPES.includes(live);
  }
  // Card #256 round 4 — helpers for the two per-order payment contact fields.
  // Both take the rendered `item` because the bulk-upload path clones every
  // toDisplay entry under a suffixed var (`order.paymentContactName__3`), so
  // the sibling field names have to be derived from whichever copy is asking.
  paymentContactSuffix = (item) => {
    const v = get(item, 'var', '');
    const i = v.indexOf('__');
    return i === -1 ? '' : v.slice(i);
  }
  // Visible exactly when this row's "Payment Required?" box is ticked — the
  // same live-value-then-saved-order fallback the checkbox's own guards use.
  // Drivers included (read-only) so a blocked driver can see who to call.
  showPaymentContact = (item) => {
    // Round 7 — master switch. Gating here (rather than on each field entry) is
    // what hides BOTH contact fields, on the single-order and the bulk-upload
    // paths alike. Tested first because the `checked` fallback below reads the
    // SAVED data.order.paymentRequired, so an order flagged while the feature was
    // on would otherwise still render its two contact fields.
    if (!paymentRequirementEnabled()) { return false }
    // Round 5 — never on a Rejection Return (the two fields in Danielle's
    // 2026-07-30 screenshot). Round 6 — nor on a Reconciliation Return.
    if (this.isPaymentExemptReturn(item)) { return false }
    const { form: { getFieldValue } } = this.props;
    const suffix = this.paymentContactSuffix(item);
    const flag = getFieldValue(`order.paymentRequired${suffix}`);
    const checked = flag === undefined
      ? !!get(this, 'state.order.data.order.paymentRequired')
      : !!flag;
    return checked && userHasRole([0, 1, 2, 3, 4], this.props.sState);
  }
  // These fields first register the moment the box is ticked, so seeding via
  // initialValue lands at exactly the right time — no setFieldsValue race with
  // a field that has not mounted yet (rc-form silently drops those, which is why
  // this must stay on initialValue and never move to setFieldsValue).
  // A value stored ON THE ORDER always wins, so contact details someone actually
  // typed are never clobbered; anything else falls through to the creator's own
  // profile, and only for the roles that own those profile fields (an admin
  // creating or editing an order for someone else must not stamp their own
  // contact details onto it).
  // Round 6 — the edit path used to stop at the stored value and return blank
  // when there wasn't one, so ticking "Payment Required?" on an already-submitted
  // order gave two empty fields (Danielle 2026-08-03). Seeding fires whenever
  // there is NO stored value rather than only on the untick -> tick transition:
  // her screenshot is an order that was ALREADY ticked with both contacts blank,
  // which a transition-only fix would not have repaired. An empty string counts
  // as "not set" — the profile copies of these fields are `required` while the
  // setting is on, so a blank order value only ever means "never populated".
  paymentContactOptions = (item) => {
    const v = get(item, 'var', '');
    const suffix = this.paymentContactSuffix(item);
    const base = suffix ? v.slice(0, v.length - suffix.length) : v;
    const stored = this.state.isEdit ? get(this, `state.order.data.${base}`) : undefined;
    const hasStored = stored !== undefined && stored !== null && `${stored}`.trim() !== '';
    if (hasStored) {
      return { initialValue: stored };
    }
    if (!userHasRole([3, 4], this.props.sState)) { return {} }
    // Round 6 — the profile fallback is the CURRENT user's own contact details,
    // so it may only be stamped onto an order that is theirs. Without this an
    // Account Admin editing a colleague's already-flagged order would seed their
    // own name and phone and write them on submit, which is exactly what the
    // note above forbids. Same ownership test the "Payment Required?" checkbox
    // already uses, so the two cannot drift: a new order has no owner yet, and a
    // legacy order with no userId is treated as the current user's rather than
    // regressing to a blank field.
    const currentUserId = get(this, 'props.sState.user.id');
    const orderUserId = get(this, 'state.order.userId');
    const creatorIsCurrent = !this.state.isEdit || !orderUserId || orderUserId === currentUserId;
    if (!creatorIsCurrent) { return {} }
    const profileKey = base === 'order.paymentContactPhone'
      ? 'paymentContactPhone'
      : 'paymentContactName';
    return { initialValue: get(this, `props.sState.user.${profileKey}`) };
  }
  // Card #256 — the "Disclaimer:" block (shown while the box is ticked) and the
  // "Payment Override:" notice (shown whenever the loaded order carries one).
  // Round 4 anchors them to the LAST payment field currently on screen: the
  // contact phone when the box is ticked, otherwise the checkbox itself — which
  // keeps the mockup's order (checkbox → name → phone → disclaimer) without
  // losing the override notice on an order whose box is clear.
  renderPaymentNotices = (item, spot) => {
    // Round 7 — master switch: no Disclaimer and no Payment Override notice
    // anywhere while the feature is off. Both anchor fields are already hidden by
    // their own gates (subText only renders inside the canDisplay branch), but the
    // Payment Override notice is driven by the SAVED data.order.paymentOverride
    // rather than by a form value, so gate the renderer itself too.
    if (!paymentRequirementEnabled()) { return null }
    // Round 5 — no Disclaimer and no Payment Override notice on a Rejection
    // Return; the whole payment block is suppressed for that order type.
    // Round 6 — a Reconciliation Return is suppressed the same way.
    if (this.isPaymentExemptReturn(item)) { return null }
    const { form: { getFieldValue } } = this.props;
    const suffix = this.paymentContactSuffix(item);
    const flag = getFieldValue(`order.paymentRequired${suffix}`);
    const checked = flag === undefined
      ? !!get(this, 'state.order.data.order.paymentRequired')
      : !!flag;
    if (checked ? spot !== 'contact' : spot !== 'checkbox') { return null }
    const override = get(this, 'state.order.data.order.paymentOverride');
    const reason = get(override, 'reason');
    if (!checked && !override) { return null }
    return (
      <div>
        {checked && (
          <div css={css(styles.paymentDisclaimer)}>
            <b>Disclaimer:</b> By checking this box, you acknowledge that this order will not be delivered until payment has been received. Standard delivery fees may still apply.
          </div>
        )}
        {!!override && (
          <div css={css(styles.paymentOverrideNotice)}>
            <div><b>Payment Override:</b> This payment was overridden by an Admin.</div>
            {!!reason && <div><b>Reason:</b> {reason}</div>}
          </div>
        )}
      </div>
    );
  }
  setFieldsOriginal = (order, eprops) => {
    const { toDisplay } = this.state;
    const {
      form: { setFieldsValue },
    } = this.props;

    if (order && order.data) { return }

    toDisplay.map(item => {
      if (!item) { return }

      const val = get(order, item.var);
      if (val !== undefined) {
        setFieldsValue({ [item.var]: item.type === 'datepicker' ? val && getMidDate(val) : val });
        if (item.type === 'routesearch') {
          get(this, [item.var, 'onChange'], () => 0)(val);
        }
      } else if (item.var === 'order.pickupType') {
        setTimeout(() => { 
          const val = get(order, 'order.orderType', 'layover');
          this.handleSelectChange(val, { var: 'order.orderType' });
        }, 500);
      } else if (item.var === 'order.signature.driver' && userHasRole([2], this.props.sState)) {
        const sig = get(this.props, 'sState.user.signature', {});
        setFieldsValue({ [item.var]: { ...sig, date: new Date().toISOString() } })
      }
    });
  };
  goBack = (shouldUpdate) => {
    const isCreate = `${get(this, 'props.history.location.pathname')}`.indexOf('dashboard/createOrder') > -1;
    if (isCreate) {
      return this.props.history.push('orders');
    }
    this.navigate(this.props.history.goBack, shouldUpdate);
  }
  processData = (data, sheet, book) => {
    try {
      const { toDisplay } = this.state;
      const td = [...toDisplay];
      const {
        form: { setFieldsValue, setFields },
        sState,
      } = this.props;

      const bulkOrders = [];
      const vals = [];

      const map = {
        'order.orderDef': 'Order Definition',
        'order.destinationLN': 'Destination License',
        'order.notes': 'Notes',
        'order.orderType': 'Order Type',
        'order.pickupLN': 'Originating License',
        'order.preferredDelivery': 'Prefered Delivery Date',
        'order.preferredPickup': 'Prefered Pickup Date',
        'order.weight': ['Weight/BoxCount', 'Box Count'],
        'billing.amount': 'Billing Amount',
        'billing.invoice': 'Billing Invoice #',
        'billing.method': 'Billing Method',
        'billing.manifest': 'Manifest',
      }

      const lns = get(this, 'LNSEARCH.state.licenseNumbersAll', []);
      const userLNS = uniqBy([
        ...[get(sState.user, 'company[License #]')],
        ...get(sState.user, 'company.licenseNumbers', [])
      ]);

      data.map((d, i) => {
        // const dat = {
        //   'Destination License': "404-44523",
        //   'Notes': "Requires refrigeration",
        //   'Order Type': "Pickup",
        //   'Originating License': "403-12345",
        //   'Prefered Delivery Date': 'Thu Mar 05 2015 23:00:00 GMT-0700 (Mountain Standard Time',
        //   'Weight/BoxCount': 2
        // }

        const dl = `${get(d, 'Destination License', '')}`;
        const isNumber = isNaN(parseInt(dl.match(/\d+/))) ? false : true;
        if (!isNumber || getConfig('orders.licIsNum')) { //find dest license number
          const ln = lns.find(l => {
            const name = get(l, 'Licensee', '').toLowerCase();
            const num = get(l, 'License #');
            const dn = dl.toLowerCase();
            if (num == dl) { return true }
            return name.indexOf(dn) > -1 ? true : false;
          })
          set(d, 'Destination License', ln ? ln['License #'] : null);
        }

        bulkOrders.push({
          divider: `Order # ${i + 1}`,
        })

        td.map(t => {
          const _var = `${t.var}__${i}`;
          const mapper = map[t.var];
          let val = Array.isArray(mapper) ? get(d, mapper.find(m => get(d, m))) : get(d, mapper);
          // Card #284 — did this value come from a CELL, before any of the
          // defaulting below rewrites it? The blackout flag must only ever name a
          // date the spreadsheet actually asked for: an unmapped datepicker
          // (Pickup Date, Drop Off Date, Delivery Date — none of which render on
          // a bulk create) still ends up as getMidDate(undefined), i.e. TODAY, and
          // flagging those would raise a blackout error for a field that is not on
          // the screen and never reaches the order.
          const fromSheet = val !== undefined && val !== null && val !== '';

          if (t.var.indexOf('pickupLN') > -1) {
            if (userLNS.indexOf(val) === -1 && !userHasRole([0, 1], this.props.sState)) {
              val = first(userLNS);
            }
          } else if (t.var.indexOf('preferredDelivery') > -1 || t.var.indexOf('preferredPickup') > -1) {
            val = getMidDate(moment(val).add(4, 'hours'));
          } else if (['orderType', 'orderDef'].find(k => t.var.includes(k))) {
            const opts = t.opts();
            const topts = opts.map(o => `${o.title}`.toLowerCase());
            const vlower = `${val}`.toLowerCase();
            const ind = topts.indexOf(vlower);

            if (ind === -1) {
              val = first(opts).value;
            } else {
              val = get(opts, [ind, 'value'], val);
            }
          }

          vals.push({
            var: _var,
            val,
            type: get(t, 'type'),
            fromSheet,
          })

          bulkOrders.push({
            ...t,
            var: _var
          })
        })
      });

      // Card #284 — blacked-out delivery dates in the spreadsheet.
      //
      // `disabledDate` greys the day out in the PICKER; it is never consulted for
      // a value written by setFieldsValue, so a blacked-out day in the file used
      // to land in the form silently. Flag it here, the moment the file is
      // parsed, using the same calendar and the same predicate the order form's
      // own gate uses (describeBlackoutForMoment / getBlackoutConfig) — no second
      // copy of the rule, and no fetch: the window cache is primed at app
      // bootstrap and re-pulled by componentDidMount.
      //
      // This is the MESSAGE half of the requirement and it is deliberately shown
      // to every role, admins included. The enforcement half lives at the submit
      // gate (canContinueFromDateCheck), because a manually-set rc-form error is
      // display-only — validateFieldsAndScroll recomputes errors from the field
      // RULES and would drop this one, exactly as it already does for the
      // "Unable to find Destination License Number" error below.
      const blackoutCfg = getBlackoutConfig();
      const blackoutFieldErrors = {};
      const blackoutMessages = [];

      this.setState({
        bulkOrders,
      }, () => {
        vals.map(v => {
          if (!v) { return }

          const blackoutField = v.type === 'datepicker' && v.fromSheet ? blackoutDateField(v.var) : null;
          if (blackoutField) {
            const dayVal = getMidDate(v.val);
            const hit = describeBlackoutForMoment(dayVal, blackoutCfg);
            if (hit) {
              const day = toBlackoutDayString(dayVal);
              blackoutMessages.push(blackoutSentence(blackoutField, day, hit));
              blackoutFieldErrors[v.var] = {
                value: dayVal,
                errors: [new Error(`${moment(day).format('ddd, MMM D, YYYY')} is blacked out: ${hit.label}.`)],
              };
            }
          }

          if (v.var.includes('destinationLN') && !v.val) {
            const hasEdit = getConfig('orders.editAddress', false);
            setFields({
              [v.var]: {
                value: null,
                errors: [new Error(`Unable to find Destination License Number. ${hasEdit ? 'Please Add a new License Number.' : ''}`)]
              },
            })
          } else {
            setFieldsValue({
              [v.var]: v.type === 'datepicker' ? getMidDate(v.val) : v.val,
            })
          }
        })

        bulkOrders.map(t => {
          const val = get(vals.find(v => v.var == t.var), 'val');

          const inputProps = get(t, 'inputProps');
          if (inputProps && typeof inputProps == 'function') {
            const ip = inputProps.call(t);
            // Card #266 — was Object.keys(ip), which blind-fired EVERY function
            // on inputProps including onSaveNew, mailing admins one bogus "New
            // License Added" notice per license cell of the spreadsheet.
            AUTO_INVOKABLE_INPUT_PROPS.map(k => {
              if (typeof ip[k] == 'function') {
                try {
                  ip[k](val, { isAuto: true });
                } catch (err) {
                  console.warn(err);
                }
              }
            })
          }
        })

        // Applied LAST, after the auto-invoked onChange handlers above: those go
        // through setFieldValue -> form.setFieldsValue, which replaces the field
        // entry and would wipe an error set before them.
        if (size(blackoutMessages)) {
          setFields(blackoutFieldErrors);
          this.setState({
            error: `${blackoutMessages.join(' ')} ${BLACKOUT_BLOCKED_MSG}`,
          });
        }

        setTimeout(() => {
          this.setState({ loading: false });
        }, 500);
      })
    } catch (err) {
      this.setState({
        loading: false,
        error: `Error: ${err.message}`
      })
    }
  }
  downloadTemplate = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const win = window.open(`${config().url}/static/media/${getConfig('orders.templateFile', 'Template.xlsx')}`, '_blank');
    win.focus();
  }
  toggleCancel = () => {
    this.setState({ cancelModal: !this.state.cancelModal, loading: false });
  }
  cancelOrder = async () => {
    try {
      const { cancelInput, order } = this.state;
      
      this.setState({ loading: true });
      set(order, 'data.cancelReason', cancelInput || 'Other');
      await cancelOrder(order);
      // Card #151 round 9 — Order Canceled email, rebuilt to Danielle's
      // 2026-08-28 mockup (Order_Cancel_Updates). Admin-only: recipients are
      // unchanged, there is no notifyUsers, no orders.contactText block, no logo
      // header and no POWERED BY footer — this never reaches a customer.
      // Against the mockup, line by line:
      //   * subject gains the tenant prefix (emails.subjectPrefix), the same
      //     single construction the other five subjects in this file use;
      //   * sentence 1 becomes the bold "The following Order has been Canceled"
      //     and the order id moves out of it into its own labelled row;
      //   * a bold-labelled "Order #:" row and a fully bold "Reason:" row are
      //     new. data.cancelReason is written two lines above from the Cancel
      //     Order? modal's Select, so there is no new field and no migration —
      //     'Other' is that Select's own default and is repeated here only for an
      //     order cancelled before the reason existed;
      //   * the rows are single spaced (one <br />), where every row used to be
      //     separated by a blank line.
      //
      // Card #151 round 10 — the View Order Here deep link is GONE, and this is
      // the one email on this screen that must not have one. Round 9 added it
      // against the mockup for consistency with Order Rejected / Order update /
      // Order Exception / Order date change, and explicitly asked Danielle to
      // confirm. She did, 2026-09-01: "I Left the link off intentionally as the
      // order is no longer available to view after its canceled so in theory it
      // will bring up a blank page." That is a correctness point, not a taste
      // one — cancelOrder() above has already removed the order by the time this
      // mail is read, and navigate(goBack) two lines below leaves the screen for
      // the same reason. The four sibling emails keep their link because their
      // orders still exist. Do not re-add it here "for consistency".
      this.sendEmail({
        to: [
          (isDev() ? null : order.createdBy),
          ...adminRecipients('Order canceled email'),
        ].filter(e => e),
        subject: [getConfig('emails.subjectPrefix'), `Order canceled (${order.orderId})`].filter(f => !!f).join(' - '),
        // Card #291 — the two company rows now render through companyLine, the
        // shared helper the three customer emails were given in this card. This
        // email is where that format came from and its output is unchanged for
        // any order that has both halves; all the helper adds here is that a
        // company with no DBA falls back to its Licensee and one with neither
        // name nor number no longer mails a bare "Vendor:  ()".
        html:
`<b>The following Order has been Canceled</b><br />
<b>Order #:</b> ${order.orderId}<br />
<b>Reason: ${get(order, 'data.cancelReason', 'Other')}</b><br />
${[
  companyLine('Vendor', get(order, 'data.pickupCompany')),
  companyLine('Destination', get(order, 'data.destCompany')),
].filter(f => !!f).join('<br />')}`
      }).catch(err => {
        console.warn('Order canceled email failed to send', err);
        try { Bugsnag.notify(err); } catch (_e) { /* diagnostics must never break the app */ }
      });
      this.toggleCancel();
      this.navigate(this.props.history.goBack, true);
    } catch (err) {
      this.setState({
        loading: false,
        error: `Error: ${err.message}`
      })
    }
  }
  checkinorder = (e) => {
    const checkedIn = orderIsCheckedIn(this.state.order);
    set(this, 'state.order.data.order.checkedin', checkedIn ? undefined : { 
      time: moment().toISOString(),
      user: get(this, 'props.sState.user.id'),
    })

    // Checking an order in also picks it up (card #260). Removing a check in
    // never touches the status.
    if (!checkedIn) {
      const status = Number(get(this, 'state.order.data.order.status'));

      if (CHECKIN_AUTO_PICKUP_FROM.includes(status)) {
        const { form: { setFieldsValue } } = this.props;

        // Both writes are required: `order.status` is not a registered form
        // field on external orders (canDisplay), so setFieldsValue silently
        // no-ops there and submit() falls back to the state value.
        setFieldsValue({ 'order.status': OrderStatus.orderPickedUp });
        set(this, 'state.order.data.order.status', OrderStatus.orderPickedUp);
      }
    }

    this.submit(e);
  }
  pickupOrder = (e) => {
    try {
      const checkedIn = orderIsCheckedIn(this.state.order);

      const conf = checkedIn ? window.confirm('This order has already been checked in. Are you sure you want to continue?') : true;

      if (conf) {
        set(this, 'state.order.data.order.checkedin', { 
          time: moment().toISOString(),
          user: get(this, 'props.sState.user.id'),
        })
      } else {
        return;
      }

      const yes = window.confirm('Are you sure you want to set this order to picked up?');
  
      if (yes) {
        const { form: { setFieldsValue } } = this.props;
        
        setFieldsValue({ 'order.status': OrderStatus.orderPickedUp });
        // set(this, 'state.order.data.order.status', OrderStatus.orderPickedUp);

        const sigKey = 'state.order.data.order.signature.pickup';
        const hasSig = get(this, sigKey);
        const userHasSig = get(this.props, 'sState.user.signature');
  
        if (!userHasSig) {
          throw new Error(`You don't have a signature currently assigned to your profile.`)
        }
  
        if (!hasSig && userHasSig) {
          set(this, sigKey, { ...userHasSig, date: moment().toISOString() });
        }
  
        this.submit(e);
      }
    } catch (err) {
      message.error(err.toString());
    }
  }
  checkoutorder = (e = {}) => {
    const { form: { getFieldValue, setFieldsValue } } = this.props;
    const checkedOut = e.forceCheckout ? false : orderIsCheckedOut(this.state.order);

    if (checkedOut) {
      set(this, 'state.order.data.order.checkedout', undefined)
      this.submit(e);
    } else {
      this.setState({
        checkoutModal: {
          visible: true,
          title: 'Checkout Order?',
          weight: getFieldValue('order.weight') || 1,
          onOk: () => {
            const weight = get(this, 'state.checkoutModal.weight', 1);
            set(this, 'state.order.data.order.weight', weight);
            set(this, 'state.order.data.order.checkedout', checkedOut ? undefined : { 
              time: moment().toISOString(),
              user: get(this, 'props.sState.user.id'),
            })
            setFieldsValue({ 'order.weight': weight });
            this.submit(e);
          },
          onCancel: () => {
            this.setState({ checkoutModal: { visible: false } });
          }
        }
      })
    }
  }
  // Card #267 — the driver-facing Box Count chip writes straight into the loaded
  // order instead of the antd form. `order.weight` is not one of the fields
  // filterBasedOnRoles() hands a driver, so it is never registered with
  // getFieldDecorator, and rc-form silently drops setFieldsValue for a name it
  // has not seen. submit() re-applies the box count from
  // state.order.data.order.weight when the form carries no value for it, so
  // writing to state is all that is needed for the driver's edit to save.
  changeBoxCount = (e) => {
    const weight = get(e, 'target.value', '');

    this.setState({
      order: {
        ...(this.state.order || {}),
        data: {
          ...get(this, 'state.order.data', {}),
          order: {
            ...get(this, 'state.order.data.order', {}),
            weight,
          },
        },
      },
    });
  }
  selectRoute = (val, item) => {
    const { form: { setFieldsValue } } = this.props;

    item.onChange && item.onChange(val);
    setFieldsValue({ [item.var]: val });
  }
  render() { 
    const { sState, isModal, form: { getFieldDecorator, setFieldsValue, getFieldValue }, screen } = this.props;
    const { 
      error,
      success,
      loading,
      order,
      bulkOrders,
      cancelModal,
      isNewAdminOrder,
      checkoutModal,
      codBlockModal,
    } = this.state;

    const orderStatus = get(order, 'data.order.status', 10);

    const isSuspended = userIsSuspended(sState);

    const showBack = order && !isModal;

    const draggerProps = {
      name: 'file',
      multiple: false,
      accept: ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
      beforeUpload: (file) => {
        this.setState({ loading: true });
        const fileReader = new FileReader();
        fileReader.onload = (e) => {
          try {
            let binary = "";
            const bytes = new Uint8Array(e.target.result);
            const length = bytes.byteLength;
            for (let i = 0; i < length; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const oFile = XLSX.read(binary, { 
              type: 'binary', 
              cellDates: true,
              cellNF: false,
              cellText: false 
            });
            const sheet1 = oFile.Sheets[oFile.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(sheet1);
            this.processData(data, sheet1, oFile);
          } catch (err) {
            message.error(`Unable to process spreadsheet: ${err.message}`);
          }
        };
        fileReader.readAsArrayBuffer(file);
      }
    };

    const toDisplay = bulkOrders.length > 0 ? bulkOrders : this.filterBasedOnRoles(this.state.toDisplay);

    const checkedIn = orderIsCheckedIn(order);
    const checkedOut = orderIsCheckedOut(order);
    const isAdmin = userHasRole([0, 1], sState);
    const isDriver = userHasRole(2, sState);
    // Card #267 — driver Box Count chip. Opt-in per instance, drivers only
    // (admins already have the 'Box count' field on the form), and hidden on
    // cash returns to match that field's own canDisplay rule.
    const showDriverBoxCount = !!order
      && !isNewAdminOrder
      && isDriver
      && !this.state.cashReturn
      && !!getConfig('orders.driverBoxCountEnabled', false);
    const storedBoxCount = get(order, 'data.order.weight');
    // Keep the input controlled: lodash get() only defaults on undefined, and a
    // null weight would flip React into uncontrolled mode mid-edit.
    const boxCountValue = storedBoxCount === undefined || storedBoxCount === null
      ? ''
      : storedBoxCount;
    // Card #151 round 6 — no "Send to users" checkbox on a Rejection /
    // Reconciliation Return: those order types never send the Customer Delivery
    // Confirmation, so offering the control would be a lie (Danielle
    // 2026-08-03). Hiding the control is only half the fix — `notify` is
    // defaulted to true on load whether or not this renders, so the
    // /signmanifest call site forces it off as well.
    // LIVE DROPDOWN FIRST, saved order second — this must resolve to the SAME
    // type the save path feeds to signManifest, which builds `merged` with the
    // form value winning. Reading the saved type first instead made the two
    // disagree the moment an Admin changed the type by hand: a saved
    // cash_return switched to layover hid the checkbox while the save still
    // sent the email (a control-less send), and a saved layover switched to
    // cash_return showed a checkbox that could no longer do anything. The
    // control now renders exactly when an email would actually go out.
    const notifyOrderType = getFieldValue('order.orderType') || get(this.state, 'order.data.order.orderType');
    const showNotify = getConfig('sigs.show') && (isDriver || isAdmin) && !!this.state.isEdit && !!size(get(this.state, 'order.data.order.signature', {})) && !isDeliveryConfirmationExcludedType(notifyOrderType);
    const hasNotified = get(this.state, 'order.data.emails.signed', false);

    if (isSuspended) {
      return (
        <div className="Profile no-print" css={css(SStyles.container, styles.container)}>
          <Alert
            message={'Your account has been suspended. Please contact GPS to reactivate your account.'}
            type={'error'}
            css={css(styles.alertMargin)}
          />
        </div>
      )
    }

    return (
      <div className="Profile no-print" css={css(SStyles.container, styles.container)}>

        {showBack && <Link css={css(`margin-right: auto;`)} onClick={this.goBack}>{`< Back`}</Link>}

        {!order && bulkOrders.length === 0 && <Upload.Dragger css={css(styles.uploadContainer)} {...draggerProps}>
          <Button
            css={css(styles.dlTemplate)}
            onClick={this.downloadTemplate}
          >
            <Icon type="download" />
            <span>Download Template</span>
          </Button>

          <p className="ant-upload-drag-icon">
            <Icon type="inbox" />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to bulk upload</p>
        </Upload.Dragger>}
        
        <ScreenTitle title={(
          <div css={css(styles.column)}>
            <div css={css(styles.row)}>
              <span>{order && !isNewAdminOrder ? "Edit Order" : "Create Order"}</span>{loading && <Icon css={css('margin-left: 5px;')} type="loading" />}
            </div>
            {order && !isNewAdminOrder && isAdmin ? (
              <div>
                <Button
                  loading={loading}
                  onClick={this.pickupOrder}
                  css={css(styles.button)}
                  disabled={loading}
                >
                  {'Order Picked Up'}
                </Button>
              </div>
            ) : null}

            {order && !isNewAdminOrder && (isAdmin || (isDriver && getConfig('checkin.driverEnabled', false))) ? (
              <div>
                <Button
                  loading={loading}
                  onClick={this.checkinorder}
                  css={css(styles.button)}
                  type={checkedIn ? 'danger' : undefined}
                  disabled={loading}
                >
                  {checkedIn ? 'Remove Check In' : 'Check In Order'}
                </Button>
              </div>
            ) : null}

            {showDriverBoxCount ? (
              <div>
                <Button
                  css={css(styles.button, styles.boxCountChip)}
                  onClick={() => this.BOXCOUNTINPUT && this.BOXCOUNTINPUT.focus()}
                >
                  {'Box Count'}
                  {/* Deliberately a text input with a numeric keypad hint rather
                      than type="number". Box count is stored as a free string and
                      orders created through the external-orders API carry values
                      like "5lbs" — a number input renders those blank, which
                      invites the driver to "fill in" an apparently empty box and
                      silently replaces the real count (submit() falls back to '1'
                      on an empty value, which would also drop the printed label
                      count and the QuickBooks box-count billing line). The admin's
                      'Box count' field is a plain Input for the same reason. */}
                  <Input
                    ref={comp => this.BOXCOUNTINPUT = comp}
                    size="small"
                    type="text"
                    inputMode="numeric"
                    placeholder={'1'}
                    disabled={loading}
                    value={boxCountValue}
                    onClick={e => stopProp(e)}
                    onChange={this.changeBoxCount}
                  />
                </Button>
              </div>
            ) : null}

            {order && !isNewAdminOrder && !isAdmin && !isDriver ? (
              <div>
                <Button
                  loading={loading}
                  onClick={this.checkoutorder}
                  css={css(styles.button)}
                  type={checkedOut ? 'danger' : undefined}
                  disabled={loading}
                >
                  {checkedOut ? 'Remove Check Out' : 'Check Out Order'}
                </Button>
              </div>
            ) : null}

            {order && !isNewAdminOrder && !isDriver && <PrintLabelsButton order={order} />}
          </div>
        )} />

        {!!error && (
          <Alert
            message={error}
            type={'error'}
            closable
            css={css(styles.alertMargin)}
            onClose={() => this.setState({ error: null })}
          />
        )}

        {!!success && (
          <Alert
            message={success}
            type={'success'}
            closable
            css={css(styles.alertMargin)}
            onClose={() => this.setState({ success: null })}
          />
        )}

        <div css={css('position: absolute; left: -999999px;')}>
          <LNSearch
            ref={comp => this.LNSEARCH = comp}
          />
        </div>

        <div css={css(SStyles.formContainer, styles.formContainer, screen.isMobile && `width: 100%;`)}>
          <Form layout="vertical" onSubmit={this.submit}>
            {toDisplay.map((item, i) => {
              if (item.divider) {
                return <div css={css(styles.divider)}>{item.divider}</div>
              }

              const canDisplay = item.canDisplay ? item.canDisplay() : true;
              const label = item.info ? (
                <span>
                  <span>{typeof item.title === 'function' ? item.title() : item.title}</span>
                  <Popover overlayStyle={{ maxWidth: 250 }} content={item.info}>
                    <Icon css={css(`color: ${colors.highlight}; margin-left: 6px;`)} type="info-circle" theme="filled" />
                  </Popover>
                </span>
              ) : typeof item.title === 'function' ? item.title() : item.title;

              const type = typeof get(item, 'type') === 'function' ? item.type() : item.type;

              const inpProps = get(item, 'inputProps', () => ({})).call(item);
              // Card #256 round 4 — allow `options` to be a function so a field
              // can compute its initialValue from live state (the payment
              // contact fields seed from the profile or the saved order).
              const itemOptions = typeof item.options === 'function' ? item.options() : item.options;

              return !canDisplay ? null : (
                <Form.Item key={i} label={label} required={item.required} css={css(item.styles)}>
                  <div title={get(inpProps, 'tooltip')}>
                    {getFieldDecorator(item.var, itemOptions)(
                      type === 'dropdown' && item.opts ? (
                        <Select id={item.var} placeholder={item.placeholder} onChange={e => this.handleSelectChange(e, item)} {...(inpProps)}>
                          {(typeof item.opts === 'function' ? item.opts() : item.opts).map((opt, i) => (
                            <Select.Option key={i} value={opt.value}>
                              {opt.title}
                            </Select.Option>
                          ))}
                        </Select>
                      ) : type === 'datepicker' ?
                      (
                        <DatePicker {...(inpProps)} />
                      ) : type === 'autocomplete' ? (
                        <AutoComplete
                          placeholder={item.placeholder}
                          dataSource={typeof item.opts === 'function' ? item.opts() : item.opts}
                        />
                      ) : type === 'routesearch' ? (
                        <RouteSearch
                          ref={comp => set(this, item.var, comp)}
                          zip={item.zip()}
                          date={get(item, 'date', () => null)(item)}
                          showAll={item.showAll()}
                          orderDef={this.getOrderDef(item)}
                          onSelect={val => {
                            this.selectRoute(val, item)
                          }}
                        />
                      ) : type === 'driversearch' ? (
                        <DriverSearch
                          ref={comp => set(this, item.var, comp)}
                          date={item.date && item.date()}
                          route={item.route && item.route()}
                          initialValue={get(order, `data.${item.var}`)}
                          onSelect={val => {
                            setFieldsValue({ [item.var]: val });
                          }}
                        />
                      ) : type === 'textarea' ? (
                        <Input.TextArea rows={4} placeholder={item.placeholder} {...inpProps} />
                      ) : type === 'lnsearch' ? (
                        <LNSearch
                          ref={comp => set(this, item.var, comp)}
                          {...inpProps}
                          onSelect={(e) => this.handleSelectChange(e, { ...item, ...inpProps })}
                        />
                      ) : type === 'sigpad' ? <Sigpad {...get(item, 'props', () => ({}))()} /> : type === 'checkbox' ? (
                        <Checkbox {...inpProps} />
                      ) : (
                        <Input placeholder={item.placeholder} {...inpProps} />
                      )
                    )}
                    {item && item.subText && item.subText(item)}
                    {item && item.extraOption && item.extraOption(item)}
                  </div>
                </Form.Item>
              );
            })}

            <Modal
              title="Cancel Order?"
              visible={cancelModal}
              confirmLoading={loading}
              onOk={this.cancelOrder}
              onCancel={this.toggleCancel}
              okText="Yes"
              cancelText="Cancel"
            >
              <p>Are you sure you want to cancel this order?</p>
              <Select style={{ width: '100%' }} placeholder="Cancel Reason" onChange={e => this.setState({ cancelInput: e })}>
                {[
                  { title: 'Vendor Cancelled' },
                  { title: 'Receiver Cancelled' },
                  { title: 'GPS Cancelled' },
                  { title: 'No Product' },
                  { title: 'Other' },
                ].map(opt => <Select.Option value={opt.title}>{opt.title}</Select.Option>)}
              </Select>
              {/* <Input placeholder="License Number" value={cancelInput} onChange={e => this.setState({ cancelInput: e.target.value })} /> */}
            </Modal>

            <Form.Item css={css`clear: both;`}>
              {showNotify && <Row>
                <Checkbox checked={this.state.notify} onChange={() => this.setState({ notify: !this.state.notify })}>{hasNotified ? "Re-Send to users" : "Send to users"}</Checkbox>
              </Row>}
              
              <div css={css(styles.buttonContainer)}>
                <Button
                  loading={loading}
                  onClick={this.submit}
                  type={"primary"}
                  css={css(styles.button)}
                >
                  {'Submit'}
                </Button>

                {order && orderStatus < OrderStatus.inTransit && !this.isAllAvail() && !isDriver && <Button
                  loading={loading}
                  onClick={this.toggleCancel}
                  outline={true}
                  css={css(styles.button)}
                >
                  {'Cancel Order'}
                </Button>}

                {userHasRole([0, 1], sState) && order && <Button
                  loading={loading}
                  onClick={this.deleteOrder}
                  css={css(styles.button, `background-color: ${colors.error};`)}
                >
                  {'Delete Order'}
                </Button>}
              </div>
            </Form.Item>
          </Form>

          <Modal {...checkoutModal}>
            <p>Please confirm this is the correct box/bundle count?</p>
            <Input value={get(this, 'state.checkoutModal.weight', 1)} onChange={e => this.setState({ checkoutModal: { ...checkoutModal, weight: e.target.value } })} />
          </Modal>

          {/* Card #256 round 2 — very visible driver popup when COD enforcement
              blocks completing a delivery (per Danielle's Popup_Window mock:
              pink body, thick red border, large bold black text). Raised by the
              client guard and by the matched server-guard error paths. Only
              dismisses — no navigation, and loading is already reset. */}
          <Modal
            visible={!!codBlockModal}
            closable={false}
            centered
            footer={null}
            width={480}
            bodyStyle={{ padding: 0 }}
            onCancel={() => this.setState({ codBlockModal: false })}
          >
            <div css={css(styles.codBlockModal)}>
              <div css={css(styles.codBlockTitle)}>PAYMENT REQUIRED to Complete Delivery</div>
              <div css={css(styles.codBlockSub)}>Call Dispatch for any questions.</div>
              <Button
                css={css(styles.codBlockButton)}
                onClick={() => this.setState({ codBlockModal: false })}
              >
                {'OK'}
              </Button>
            </div>
          </Modal>
        </div>
      </div>
    );
  }
}

export const Order = ScreenComponent(Form.create()(withRouter(OrderComponent)));
